// ControlsManager.util.ts

import { db } from '../../config/main_config'; // Adjust the path as needed
import { createToast } from './createToast.util'; // Utility for showing toast notifications
import { waitForCompanyID } from './waitForCompanyID.util';
import Tableordion from './tableordion.util';
import { nameFromUID } from './nameFromUID.util';
import { findFrameworkName } from './findFrameworkName.util';
import { sanitise } from "./sanitise.util";
import { addPagerToTables, addPagerToTable } from '../tablePages.module';
import anime from 'animejs';
import { getAssignees } from '../getAssignees.module';
import { closeModal, openModal, waitForWindowProperty } from './routerAdditives.util';
import Swal from 'sweetalert2';

(window as any).getAssignees = getAssignees;

// Extend Window interface to include our custom properties
declare global {
    interface Window {
        mcmControls: ControlData[];
        configurationStandards: ConfigurationStandards;
        quickLookup: { [key: string]: any };
        changePage: (page: number) => void;
    }
}

async function getMasterControlMappingTasks(controlID: string): Promise<string[]> {
    try {
        // Ensure controlID is a string
        const docId = String(controlID);
        // console.log('[MCM Debug] Fetching tasks for control:', docId);

        const doc = await db.collection('masterControlMapping').doc(docId).get();
        // console.log('[MCM Debug] Document exists:', doc.exists);

        if (doc.exists) {
            const data = doc.data();
            // console.log('[MCM Debug] Document data:', data);

            // If tasks array exists, return it
            if (data && Array.isArray(data.tasks)) {
                // console.log('[MCM Debug] Found tasks:', data.tasks);
                return data.tasks;
            }

            // console.log('[MCM Debug] No tasks array in document');
            return [];
        }

        console.log('[MCM Debug] Document does not exist');
        return [];
    } catch (error) {
        console.error('Error fetching masterControlMapping tasks:', error);
        return [];
    }
}

function renderArray(arr, property = null, tag = null, checkForMCMTasks = false, controlID: boolean | string = false, skipPlaceholderAndJustReturnCode = false) {
    // If skipPlaceholderAndJustReturnCode is true, generate and return the code immediately
    if (skipPlaceholderAndJustReturnCode) {
        return generateCode(arr, property, tag, checkForMCMTasks, controlID);
    }

    // Otherwise, return a placeholder immediately
    const placeholder = '<span class="loading-placeholder">Loading...</span>';
    const placeholderId = `placeholder-${Math.random().toString(36).substr(2, 9)}`;
    const placeholderElement = `<span id="${placeholderId}">${placeholder}</span>`;

    // Process the array in the background
    (async () => {
        try {
            let code = await generateCode(arr, property, tag, checkForMCMTasks, controlID);

            // Use requestAnimationFrame to ensure the placeholder is in the DOM
            requestAnimationFrame(() => {
                const placeholders = document.querySelectorAll(`#${placeholderId}`);
                placeholders.forEach(placeholder => {
                    placeholder.outerHTML = code;
                });
            });
        } catch (error) {
            console.error('Error in renderArray:', error);
            const placeholders = document.querySelectorAll(`#${placeholderId}`);
            placeholders.forEach(placeholder => {
                placeholder.outerHTML = '<span class="error-state">Error loading content</span>';
            });
        }
    })();

    return placeholderElement;
}

async function generateCode(arr, property, tag, checkForMCMTasks, controlID) {
    let link = "#";
    if (tag !== null || checkForMCMTasks) {
        if (tag === null && checkForMCMTasks) tag = "ASS";
        const origin = window.location.origin;
        const routes = {
            "RSK": "/riskregister",
            "OBJ": "/managementSystemGuide#!/MSG/planning/objectives",
            "LEO": "/managementSystemGuide#!/MSG/context/legalObligations",
            "AST": "/assetregister",
            "ASS": "/assurance-actions",
            "SUP": "/supportDocs",
            "POL": "/policy-admin"
        };
        link = origin + (routes[tag] || "#");
    }

    let tmpArray = arr;

    // For tasks, combine with masterControlMapping tasks
    if (checkForMCMTasks && controlID !== false) {
        if (controlID) {
            const masterTasks = await getMasterControlMappingTasks(controlID);
            tmpArray = [...new Set([...tmpArray, ...masterTasks])];
        }
    }

    // Generate the code
    let code = '';
    if (tmpArray && typeof tmpArray.map === 'function') {
        code = tmpArray.map(item => {
            if (tag === "ASS" || tag === "POL") tag = "";
            if (typeof item === 'object' && !Array.isArray(item)) {
                let content = property ? item[property] : Object.values(item).join(', ');
                if (property === 'standard') {
                    return `<span class="badge">${tag ? `${tag}-${sanitise(content)}` : sanitise(content)}</span>`;
                }
                return `<a class="hover-info" href="${link}?id=${sanitise(content)}">${tag ? `${tag}-${sanitise(content)}` : sanitise(content)}</a>`;
            }
            if (property === 'standard') {
                return `<span class="badge">${tag && !sanitise(item).includes(tag) ? `${tag}-${sanitise(item)}` : sanitise(item)}</span>`;
            }
            return `<a class="hover-info" href="${link}?id=${sanitise(item)}">${tag && !sanitise(item).includes(tag) ? `${tag}-${sanitise(item)}` : sanitise(item)}</a>`;
        }).join(property === 'standard' ? ' ' : ', ');
    } else if (typeof tmpArray === 'string') {
        code = tmpArray;
    } else {
        console.error('tmpArray doesn\'t have .map function', tmpArray);
        code = '';
    }

    return code;
}


interface ControlData {
    standard: string;
    section: string;
    topic: string;
    controlID: string;
    controlDescription: string;
    controlOwner: string;
    assets?: string[];
    tasks?: string[];
    supportingDocuments?: string[];
    policies?: string[];
    relatedConfigurationStandard?: string[];
    risks?: string[];
    relatedControls?: string[];
    ismsEntries?: string[];
    ismsObjectives?: string[];
    legalObligations?: string[];
    [key: string]: any;
}

interface ConfigurationStandard {
    configurationStandard: string;
    controls: ControlData[];
    [key: string]: any;
}

interface StandardData {
    standard: string;
    controls: StandardControlData[];
}

interface StandardControlData {
    standard: string;
    section: string;
    topic: string;
    controlID: string;
    controlDescription: string;
    controlOwner: string;
    controlApplicable: boolean;
    isISM?: boolean;
}

interface Standards {
    [key: string]: StandardData;
}

interface GlobalControl {
    [key: string]: {
        [key: string]: any;
    };
}

interface ModuleSettings {
    Enabled?: boolean | string;
    enabled?: boolean | string;
    active?: boolean | string;
    Active?: boolean | string;
    [key: string]: any;
}

interface ConfigurationStandards {
    [key: string]: ConfigurationStandard;
}

interface ControlsResponse {
    ConfigurationStandards: ConfigurationStandards;
    Controls: ControlData[];
}

const frameworkToFriendly = {
    'iso270012013AnnexAControls': 'ISO 27001',
    'ACSCE8MM112022IDControls': 'Essential 8',
    'ACSCE8MM102021IDControls': 'Essential 8',
    'DISPControls': 'DISP',
    'E8 Controls': 'Essential 8',
    'E8Controls': 'Essential 8',
    'SOC2Controls': 'SOC 2',
    'iso270012022AnnexAControls': 'ISO 27001',
    'iso270012022Controls': 'ISO 27001',
    'nist-800-171rev2': 'NIST 800-171',
    'nist-csf-rev1.1': 'NIST CSF',
    'nist800-53-rev5': 'NIST 800-53',
    'paControls': 'Privacy Act',
    'ism': "ISM",
    "smb1001": "SMB 1001",
    "smb1001Controls": "SMB 1001",
    "qgov-is18-controls": "QGOV IS18",
    // Add more mappings here if needed
};

const complianceCache = {};

async function waitForSelector(selector: string): Promise<void> {
    return new Promise((resolve, reject) => {
        const interval = setInterval(() => {
            const element = document.querySelector(selector);
            if (element) {
                clearInterval(interval);
                resolve();
            }
        }, 100);
        setTimeout(() => {
            clearInterval(interval);
            reject(new Error(`Selector "${selector}" not found`));
        }, 10000);
    });
}

// Show loading screen
function showLoadingScreen() {
    let loadingOverlay = document.getElementById('loadingOverlay');
    if (!loadingOverlay) {
        loadingOverlay = document.createElement('div');
        loadingOverlay.id = 'loadingOverlay';
        loadingOverlay.style.position = 'fixed';
        loadingOverlay.style.top = '0';
        loadingOverlay.style.left = '0';
        loadingOverlay.style.width = '100%';
        loadingOverlay.style.height = '100%';
        loadingOverlay.style.backgroundColor = 'rgba(255, 255, 255, 0.8)';
        loadingOverlay.style.zIndex = '1000';
        loadingOverlay.style.display = 'flex';
        loadingOverlay.style.justifyContent = 'center';
        loadingOverlay.style.alignItems = 'center';
        loadingOverlay.innerHTML = '<div class="spinner"></div>'; // Define spinner styles in CSS
        document.body.appendChild(loadingOverlay);
    } else {
        loadingOverlay.style.display = 'flex';
    }
}

// Hide loading screen
function hideLoadingScreen() {
    const loadingOverlay = document.getElementById('loadingOverlay');
    if (loadingOverlay) {
        loadingOverlay.style.display = 'none';
    }
}

async function loadComplianceCache(path = '/compliance/', cache = {}, subcollections = ['controls']) {
    try {
        const collectionRef = db.collection(path);
        const collectionSnapshot = await collectionRef.get();

        if (!collectionSnapshot.empty) {
            const promises = collectionSnapshot.docs.map(async (doc) => {
                const docData = doc.data();
                const docId = doc.id;

                cache[docId] = docData;

                const subcollectionPromises = subcollections.map(async (subcollectionId) => {
                    const subcollectionRef = db.collection(`${path}${docId}/${subcollectionId}`);
                    const subcollectionSnapshot = await subcollectionRef.get();

                    if (!subcollectionSnapshot.empty) {
                        cache[docId][subcollectionId] = {};
                        subcollectionSnapshot.forEach(subDoc => {
                            if (subcollectionId === 'controls') {
                                cache[docId][subDoc.id] = subDoc.data();
                            } else {
                                cache[docId][subcollectionId][subDoc.id] = subDoc.data();
                            }
                        });
                    }
                });
                await Promise.all(subcollectionPromises);
            });
            await Promise.all(promises);

            (window as any).complianceCache = cache;
        }
    } catch (error) {
        console.error('Error loading compliance cache:', error);
    }
}

async function pullFromGlobalCompliance(control) {
    try {
        const cacheKey = control.controlNumber || control.controlID;

        const cachedDocId = Object.keys((window as any).complianceCache).find((docId) => {
            const data = (window as any).complianceCache[docId];
            const keys = Object.keys(data);
            if (typeof data === 'object' && !data.hasOwnProperty('controlNumber') && !data.hasOwnProperty('controlID')) {
                for (let i = 0; i < keys.length; i++) {
                    if (keys[i] === cacheKey) return true;
                    continue;
                }
            }
            return data.controlNumber === cacheKey || data.controlID === cacheKey;
        });

        if (cachedDocId) {
            const complianceData = (window as any).complianceCache[cachedDocId];
            return complianceData[cacheKey];
        }

        await loadComplianceCache();

        const retryCachedDocId = Object.keys((window as any).complianceCache).find((docId) => {
            const data = (window as any).complianceCache[docId];
            const keys = Object.keys(data);
            if (typeof data === 'object' && !data.hasOwnProperty('controlNumber') && !data.hasOwnProperty('controlID')) {
                for (let i = 0; i < keys.length; i++) {
                    if (keys[i] === cacheKey) return true;
                    continue;
                }
            }
            return data.controlNumber === cacheKey || data.controlID === cacheKey;
        });

        if (retryCachedDocId) {
            const complianceData = (window as any).complianceCache[retryCachedDocId];
            return complianceData[cacheKey];
        }

        return {};
    } catch (error) {
        console.error(
            `Error pulling from global compliance for control ${control.controlNumber}:`,
            error,
        );
        return {};
    }
}

export const usableCache = (window as any).complianceCache || loadComplianceCache().then(cache => cache);

function truncateTo15Words(input: string): string {
    if (input !== undefined && input.trim().length > 0) {
        const words = input.trim().split(/\s+/);
        if (words.length <= 15) {
            return input;
        }
        const truncated = words.slice(0, 15).join(' ');
        return `${truncated}...`;
    } else {
        return input;
    }
}

const initBulkModal = async () => {
    const tabButtons = document.querySelectorAll("[data-managedid='bulkOperations'] .nav-link");
    const tabPanes = document.querySelectorAll("[data-managedid='bulkOperations'] .tab-pane");

    // Get the control IDs that are being updated
    const controlIDs = (window as any).mcmControlsToUpdate || [];

    // Get the existing mapped items for these controls
    const existingMappings = {
        assets: new Set(),
        tasks: new Set(),
        supportingDocuments: new Set(),
        policies: new Set(),
        relatedConfigurationStandard: new Set(),
        risks: new Set(),
        relatedControls: new Set(),
        ismsEntries: new Set(),
        ismsObjectives: new Set(),
        legalObligations: new Set(),
        suppliers: new Set()
    };

    const checkboxNames = {
        assets: "assetCheckboxes",
        tasks: "taskCheckboxes",
        supportingDocuments: "docCheckboxes",
        policies: "policyCheckboxes",
        relatedConfigurationStandard: "relatedConfigCheckboxes",
        risks: "riskCheckboxes",
        relatedControls: "relatedControlCheckboxes",
        ismsEntries: "ismsEntryCheckboxes",
        ismsObjectives: "ismsObjectiveCheckboxes",
        legalObligations: "legalObligationCheckboxes",
        suppliers: "supplierCheckboxes",
    }

    // Fetch and combine all existing mappings
    for (const controlID of controlIDs) {
        const control = (window as any).quickLookup[controlID];
        if (control) {
            Object.keys(existingMappings).forEach(key => {
                if (Array.isArray(control[key])) {
                    control[key].forEach(item => existingMappings[key].add(item));
                }
            });
        }
    }

    // Pre-check existing mappings in the modal
    Object.keys(existingMappings).forEach(key => {
        const checkboxes = document.querySelectorAll(`input[name="${checkboxNames[key]}"]`);
        checkboxes.forEach((checkbox: HTMLInputElement) => {
            const valuesSet = existingMappings[key]; // This is a Set<string>

            if (!(valuesSet instanceof Set)) {
                console.error(`Error: existingMappings[${key}] is not a Set`, valuesSet);
                return;
            }

            console.log("With Prefix (Set):", valuesSet);
            console.log("Checkbox Value:", checkbox.value);

            // Remove prefix from each value in the Set
            const cleanedValues = new Set(
                Array.from(valuesSet).map(value =>
                    typeof value === "string" && value.includes('-') ? value.split('-')[1] : value
                )
            );

            console.log("Prefix Removed (Set):", cleanedValues);

            if (cleanedValues.has(checkbox.value)) {
                checkbox.checked = true;
            }
        });
    });

    tabButtons.forEach((button) => {
        button.addEventListener("click", function () {
            tabButtons.forEach((btn) => btn.classList.remove("active"));
            tabPanes.forEach((pane) => pane.classList.remove("show", "active"));

            this.classList.add("active");

            const targetPaneId = this.getAttribute("data-deit-target");
            const targetPane = document.querySelector(targetPaneId);
            if (targetPane) {
                targetPane.classList.add("show", "active");
            }
        });
    });
}



// Store previously selected controls
(window as any).previouslySelectedControls = []; /* DEV-2222 Fix */

(window as any).openBulkConfigModal = async function () {
    // If there were previously selected controls and current selection is from individual operation,
    // restore the previous selection
    /* DEV-2222 Fix */
    if ((window as any).previouslySelectedControls.length > 0 &&
        (window as any).mcmControlsToUpdate?.length === 1) {
        (window as any).mcmControlsToUpdate = [...(window as any).previouslySelectedControls];
        forceCountUpdate();
    }
    await initBulkModal();
    openModal("[data-managedid='bulkOperations']");
};

(window as any).individualOperations = async function (controlID: string) {
    // Store current selection before changing to individual control
    if ((window as any).mcmControlsToUpdate?.length > 1) { /* DEV-2222 Fix */
        (window as any).previouslySelectedControls = [...(window as any).mcmControlsToUpdate];
    }
    (window as any).mcmControlsToUpdate = [controlID];
    forceCountUpdate();
    await initBulkModal();
    openModal("[data-managedid='bulkOperations']");
};

(window as any).addConfigurationStandards = async () => {
    // Ask for the name of the new configuration standard
    const { value: configStandardName } = await Swal.fire({
        title: 'Create Configuration Standard',
        input: 'text',
        inputLabel: 'Enter the name for the new Configuration Standard',
        inputPlaceholder: 'e.g., ISO 27001 - Annex A',
        showCancelButton: true,
    });

    if (!configStandardName) {
        return; // Exit if no input
    }

    const controlsToUpdate = (window as any).mcmControlsToUpdate || [];
    let selectedControls = controlsToUpdate;

    // Validate that controls are selected when using bulk operations
    if (selectedControls.length === 0) {
        // If no controls are selected from bulk operations, show unassigned controls
        const unassignedControls = Object.entries((window as any).controls || {}).flatMap(([standard, controls]) =>
            Object.values(controls as Record<string, ControlData>).filter(
                (control: ControlData) =>
                    !control.relatedConfigurationStandard ||
                    control.relatedConfigurationStandard.join(',').toLowerCase().trim() === 'unassigned' ||
                    control.relatedConfigurationStandard.join(',').trim() === ''
            )
        );

        console.log('Controls to show', unassignedControls)
        // Construct tableordion layout for unassigned controls
        const controlsTableordion = `
            <table class="tableordion">
                <thead>
                    <tr class="tableordion-header">
                        <th>Select</th>
                                <th>Control ID</th>
                                <th>Description</th>
                                <th>Owner</th>
                    </tr>
                </thead>
                <tbody class="tableordion-body">
                ${unassignedControls
                .map(
                    (control: ControlData) => `
                        <tr class="tableordion-row">
                            <td>
                                <div class="checkbox">
                                <label for="${control.controlID}">
                                    <input type="checkbox" id="${control.controlID}" value="${control.controlID}">
                                    <span class="deit-checkbox"></span>
                                </label>
                            </div>
                            </td>
                            <td>${control.controlID}</td>
                            <td>${truncateTo15Words(control.controlDescription)}</td>
                            <td>${control.controlOwner || 'Unassigned'}</td>
                        </tr>
                        `
                )
                .join('')}
                </tbody>
            </table>
        `;

        // Let the user pick controls to assign
        const { value: pickedControls, dismiss } = await Swal.fire({
            title: 'Select Controls',
            html: controlsTableordion,
            focusConfirm: false,
            customClass: {
                container: 'selectControls-container'
            },
            preConfirm: () => {
                const selectedControls = Array.from(document.querySelectorAll('input[type="checkbox"]:checked')).map(
                    (input) => (input as HTMLInputElement).value
                );
                if (selectedControls.length === 0) {
                    Swal.showValidationMessage('Please select at least one control');
                    return false;
                }
                return selectedControls;
            },
            showCancelButton: true,
            didRender: () => {
                // Initialize tableordion and add pager after rendering
                new Tableordion('.swal2-container .tableordion-row');
                addPagerToTable(document.querySelector('.swal2-container .tableordion'));
            },
        });

        // If user clicked cancel or closed the modal
        if (dismiss) {
            return;
        }

        // If no controls were picked (should not happen due to validation)
        if (!pickedControls || pickedControls.length === 0) {
            createToast({
                message: 'Please select at least one control to create a Configuration Standard.',
                customStyles: {
                    backgroundColor: '#D8090F',
                    color: '#ffffff',
                },
            });
            return;
        }

        selectedControls = pickedControls;
    }

    // Assign the new configuration standard to the selected controls
    const updatePromises = selectedControls.map((controlID: string) =>
        saveControl(controlID, { relatedConfigurationStandard: [configStandardName] }, true, true)
    );

    try {
        await Promise.all(updatePromises);

        // Update UI and success notification
        createToast({
            message: `Configuration Standard "${configStandardName}" added to selected controls.`,
            customStyles: {
                backgroundColor: '#07B824',
                color: '#ffffff',
            },
        });

        // Force UI update or reload controls
        forceCountUpdate();
        setupControlsListener(true);
    } catch (error) {
        console.error('Error assigning configuration standard:', error);

        createToast({
            message: `Failed to assign Configuration Standard.`,
            customStyles: {
                backgroundColor: '#D8090F',
                color: '#ffffff',
            },
        });
    }
};

function forceCountUpdate() {
    if ((window as any).mcmControlsToUpdate && (window as any).mcmControlsToUpdate.length > 0) {
        const totalSelected = (window as any).mcmControlsToUpdate.length;
        const countElements = document.querySelectorAll('.selectedCount .count');

        if (countElements.length >= 1) {
            countElements.forEach(countElement => {
                countElement.textContent = totalSelected.toString();
            });
        }
    }
}

/**
 * Helper: Safely add standards into `configurationStandards`.
 * - Accepts either a string or array of standards.
 * - Trims each standard.
 * - Treats empty/blank strings as "Unassigned".
 * - Ensures each standard key is initialized before pushing `controlData`.
 */
function addStandards(standardsInput, configurationStandards, controlData) {
    // Convert to array if it's a string or fall back to empty array if nothing
    let standardsArray = [];
    if (typeof standardsInput === 'string') {
        standardsArray = standardsInput.split(',');
    } else if (Array.isArray(standardsInput)) {
        standardsArray = standardsInput;
    }

    // Trim each standard; filter out empty ones or tag them as "Unassigned"
    standardsArray
        .map((s) => s.trim() || 'Unassigned')
        .forEach((std) => {
            if (!configurationStandards[std]) {
                configurationStandards[std] = {
                    configurationStandard: std,
                    controls: [],
                };
            }
            configurationStandards[std].controls.push(controlData);
        });
}

async function getISMControls(): Promise<any> {
    const companyID = await waitForCompanyID();
    let controls: {
        [key: string]: object;
    } = {};
    const collectionPath = `/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsSoa/ism`;
    const collectionRef = db.collection(collectionPath);

    try {
        const snapshot = await collectionRef.get();
        const controlPromises = snapshot.docs.map(async (doc) => {
            const control = doc.data();

            if (typeof control === 'object' && control.controlID) {
                if (!control.framework) { control.framework = 'ISM'; control.isISM = true; }

                // controls.push(control);
                if (!controls[control.controlID]) {
                    controls[control.controlID] = control;
                }
            }
        });

        await Promise.all(controlPromises);
        return controls;
    } catch (error) {
        console.error('Error fetching ISM Controls:', error);
    }
}


export async function fetchControls(useISM: boolean = true): Promise<ControlsResponse> {
    showLoadingScreen(); // Show loading screen
    if (!window.assignees) {
        // First get enabled modules from companySettings
        const companyID = await waitForCompanyID();
        const companySettingsRef = db.doc(`/customers/${companyID}/companySettings/complianceModules`);
        const companySettingsDoc = await companySettingsRef.get();
        const enabledModules = new Set();

        if (companySettingsDoc.exists) {
            const data = companySettingsDoc.data();
            if (data) {
                Object.entries(data).forEach(([module, keys]) => {
                    // Check various possible enabled flags
                    if (typeof keys === 'object' && keys !== null) {
                        let settings: ModuleSettings = keys;
                        const isEnabled =
                            settings.Enabled === true || settings.Enabled === 'true' ||
                            settings.enabled === true || settings.enabled === 'true' ||
                            settings.active === true || settings.active === 'true' ||
                            settings.Active === true || settings.Active === 'true';

                        if (isEnabled) {
                            enabledModules.add(module.toLowerCase());
                        }
                    }
                });
            }
        }

        console.log('Enabled modules:', enabledModules);

        await getAssignees();
    }
    return await waitForWindowProperty('assignees').then(async () => {
        const companyID = await waitForCompanyID();
        let controls: ControlData[] = [];
        let configurationStandards: { [key: string]: ConfigurationStandard } = {};

        if (useISM) {
            const collectionPath = `/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsSoa/ism`;
            const collectionRef = db.collection(collectionPath);

            try {
                const snapshot = await collectionRef.get();
                const controlPromises = snapshot.docs.map(async (doc) => {
                    const data = doc.data();

                    const controlOwnerName = await nameFromUID(data.controlOwner) || 'N/A';

                    const controlData: ControlData = {
                        standard: 'ISM',
                        section: data.section || 'N/A',
                        topic: data.topic || 'N/A',
                        controlID: data.controlID || 'N/A',
                        controlDescription: data.description || data.controlDescription || 'N/A',
                        controlOwner: controlOwnerName,
                        assets: data.assets || [],
                        tasks: data.tasks || [],
                        supportingDocuments: data.supportingDocuments || [],
                    };

                    // Check if all critical fields are N/A
                    const allFieldsNA =
                        (!controlData.section || controlData.section === 'N/A') &&
                        (!controlData.topic || controlData.topic === 'N/A') &&
                        (!controlData.controlID || controlData.controlID === 'N/A') &&
                        (!controlData.controlDescription || controlData.controlDescription === 'N/A');

                    // STop processing this control
                    if (allFieldsNA) return

                    // Check if customer data has `relatedConfigurationStandard`
                    if (data.hasOwnProperty('relatedConfigurationStandard') && data.relatedConfigurationStandard) {
                        let relatedStandards = Array.isArray(data.relatedConfigurationStandard)
                            ? data.relatedConfigurationStandard.map((standard) => standard.trim()).filter(Boolean)
                            : typeof data.relatedConfigurationStandard === 'string'
                                ? data.relatedConfigurationStandard.split(',').map((standard) => standard.trim()).filter(Boolean)
                                : [];

                        // Ensure empty arrays are assigned to "Unassigned"
                        if (relatedStandards.length === 0) {
                            relatedStandards = ["Unassigned"];
                        }

                        relatedStandards.forEach((standard) => {
                            if (!configurationStandards.hasOwnProperty(standard)) {
                                configurationStandards[standard] = {
                                    configurationStandard: standard,
                                    controls: [],
                                };
                            }
                            configurationStandards[standard].controls.push(controlData);
                        });
                    } else {
                        // Use global compliance data if customer data is missing `relatedConfigurationStandard`
                        const globalControlData = await pullFromGlobalCompliance(controlData);
                        if (globalControlData.hasOwnProperty('relatedConfigurationStandard') && globalControlData.relatedConfigurationStandard) {
                            let relatedStandards = Array.isArray(globalControlData.relatedConfigurationStandard)
                                ? globalControlData.relatedConfigurationStandard.map((standard) => standard.trim()).filter(Boolean)
                                : typeof globalControlData.relatedConfigurationStandard === 'string'
                                    ? globalControlData.relatedConfigurationStandard.split(',').map((standard) => standard.trim()).filter(Boolean)
                                    : [];

                            // Ensure empty arrays are assigned to "Unassigned"
                            if (relatedStandards.length === 0) {
                                relatedStandards = ["Unassigned"];
                            }

                            relatedStandards.forEach((standard) => {
                                if (!configurationStandards.hasOwnProperty(standard)) {
                                    configurationStandards[standard] = {
                                        configurationStandard: standard,
                                        controls: [],
                                    };
                                }
                                configurationStandards[standard].controls.push(controlData);
                            });
                        }
                    }

                    controls.push(controlData);
                });

                await Promise.all(controlPromises);

            } catch (error) {
                console.error('Error fetching controls from ismsSoa/ism:', error);
                createToast({
                    message: 'Failed to load controls from ISM.',
                    customStyles: {
                        backgroundColor: '#D8090F',
                        color: '#ffffff',
                    },
                });
            }
        } else {
            const docPath = `/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsSoa`;
            const docRef = db.doc(docPath);

            try {
                const doc = await docRef.get();
                if (doc.exists) {
                    const data = doc.data();
                    if (data) {
                        let ismControls = await getISMControls();
                        // Merge controls from ismsSoa, ISM, and global compliance
                        let allControls = { ...data, ...ismControls };
                        const localGlobalControls = (window as any).complianceCache || {};

                        // Add missing controls from global compliance
                        Object.entries(localGlobalControls).forEach(([standard, controls]) => {
                            Object.entries(controls).forEach(([controlId, controlData]) => {
                                if (!allControls[controlId]) {
                                    allControls[controlId] = {
                                        ...controlData,
                                        framework: standard,
                                        isFromGlobalCompliance: true
                                    };
                                }
                            });
                        });
                        // console.error('All Controls', allControls)
                        const controlPromises = Object.keys(allControls).map(async (key) => {
                            // Get enabled modules from companySettings
                            const companyID = await waitForCompanyID();
                            const companySettingsRef = db.doc(`/customers/${companyID}/companySettings/complianceModules`);
                            const companySettingsDoc = await companySettingsRef.get();
                            const enabledModules = new Set();

                            if (companySettingsDoc.exists) {
                                const data = companySettingsDoc.data();
                                if (data) {
                                    Object.entries(data).forEach(([module, keys]) => {
                                        // Check various possible enabled flags
                                        if (typeof keys === 'object' && keys !== null) {
                                            let settings: ModuleSettings = keys;
                                            const isExplicitlyDisabled =
                                                settings.Enabled === false || settings.Enabled === 'false' ||
                                                settings.enabled === false || settings.enabled === 'false' ||
                                                settings.active === false || settings.active === 'false' ||
                                                settings.Active === false || settings.Active === 'false';

                                            if (!isExplicitlyDisabled) enabledModules.add(module.toLowerCase());
                                        }
                                    });
                                }
                            }

                            // Skip certain keys
                            if (key === 'someMetaDataField') return;

                            const control = allControls[key];

                            // Only process objects that look like actual controls
                            if (typeof control === 'object' && control.controlID) {
                                // Convert framework to a friendlier name if possible
                                control.framework = control.framework
                                    ? frameworkToFriendly[control.framework] || control.framework
                                    : await findFrameworkName(control.controlID);

                                // Skip controls from disabled modules
                                const standardLower = control.framework?.toLowerCase() || '';
                                // Skip if no framework
                                if (!standardLower) return;

                                // Get module settings
                                const moduleSettings = data && data[standardLower];

                                // Consider enabled unless explicitly disabled
                                const isExplicitlyDisabled = moduleSettings && typeof moduleSettings === 'object' && (
                                    moduleSettings.Enabled === false || moduleSettings.Enabled === 'false' ||
                                    moduleSettings.enabled === false || moduleSettings.enabled === 'false' ||
                                    moduleSettings.active === false || moduleSettings.active === 'false' ||
                                    moduleSettings.Active === false || moduleSettings.Active === 'false'
                                );

                                // Skip if explicitly disabled
                                if (isExplicitlyDisabled) {
                                    return;
                                }

                                // Resolve control owner name
                                const controlOwnerName = control?.controlOwner
                                    ? (await nameFromUID(control.controlOwner)) || 'Unassigned'
                                    : 'Unassigned';

                                // Build our controlData object
                                const controlData = {
                                    standard: control.framework || 'N/A',
                                    section: 'N/A',
                                    topic: 'N/A',
                                    controlID: control.controlID || 'N/A',
                                    controlDescription: control.controlDesc || control.description || control.controlJustification || 'N/A',
                                    controlApplicable: control.controlApplicable || false,
                                    controlOwner: controlOwnerName,
                                    assets: control.assets || [],
                                    tasks: control.tasks || [],
                                    supportingDocuments: control.supportingDocuments || [],
                                    policies: control.policies || [],
                                    relatedConfigurationStandard: control.relatedConfigurationStandard || [],
                                    risks: control.risks || [],
                                    relatedControls: control.relatedControls || [],
                                    ismsEntries: control.ismsEntries || [],
                                    ismsObjectives: control.ismsObjectives || [],
                                    legalObligations: control.legalObligations || [],
                                    isISM: control.isISM || false
                                };

                                // If the customer data has `relatedConfigurationStandard`, use it
                                if (control.hasOwnProperty('relatedConfigurationStandard') && control.relatedConfigurationStandard) {
                                    console.error('HAS relatedConfigurationStandard');

                                    // Add standards to our master `configurationStandards`
                                    addStandards(control.relatedConfigurationStandard, configurationStandards, controlData);

                                } else {
                                    // Otherwise, pull from global compliance
                                    const globalControlData = await pullFromGlobalCompliance(controlData);

                                    if (globalControlData?.relatedConfigurationStandard) {
                                        addStandards(globalControlData.relatedConfigurationStandard, configurationStandards, controlData);
                                    }
                                }

                                // In either case, we push this controlData to `controls`
                                controls.push(controlData);
                            }
                        });

                        await Promise.all(controlPromises);
                    }
                }
            } catch (error) {
                console.error('Error fetching controls from ismsSoa:', error);
                createToast({
                    message: 'Failed to load controls from SOA.',
                    customStyles: {
                        backgroundColor: '#D8090F',
                        color: '#ffffff',
                    },
                });
            }
        }

        hideLoadingScreen(); // Hide loading screen after fetching
        console.error("All Controls", controls, "called by")
        return { ConfigurationStandards: configurationStandards, Controls: controls };
    });
}

// Add pagination state
let currentPage = 1;
let itemsPerPage = 50; // Default value

async function fetchControlsForStandard(standardName: string): Promise<ControlData[]> {
    const { Controls } = await fetchControls();
    return Controls.filter(control => control.standard === standardName);
}

export async function renderControlsTable(controls: ControlData[] | ConfigurationStandards, configStandards = false, expanded = false, loadedStandards: Set<string> = new Set()): Promise<void> {
    showLoadingScreen(); // Show loading screen
    const tableContainer = document.querySelector('.configurationStandardsList--list');

    if (!tableContainer) {
        console.error('Controls table container not found.', configStandards ? '.tableordion-body' : '.configurationStandardsList--list');
        hideLoadingScreen();
        return;
    }

    // Clear existing content
    tableContainer.innerHTML = '';

    // Add pagination controls
    const paginationControls = document.createElement('div');
    paginationControls.style.cssText = 'display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; padding: 10px;';

    // Items per page selector
    const itemsPerPageControl = document.createElement('div');
    if (window.location.pathname === "/masterControlMapping.html" || window.location.pathname === "/masterControlMapping" || window.location.pathname === "/mcm") {
        itemsPerPageControl.innerHTML = `
        <label style="margin-right: 10px;">Controls per page:</label>
        <select id="itemsPerPageSelect" style="padding: 5px; border-radius: 4px;">
            <option value="25">25</option>
            <option value="50" selected>50</option>
            <option value="100">100</option>
            <option value="200">200</option>
        </select>
    `;
    }

    // Page navigation
    const pageNavigation = document.createElement('div');
    pageNavigation.style.cssText = 'display: flex; align-items: center; gap: 10px;';

    if (document.querySelector('.page-content').querySelector("#itemsPerPageSelect") === null) document.querySelector('.page-content').appendChild(itemsPerPageControl);
    paginationControls.appendChild(pageNavigation);
    tableContainer.appendChild(paginationControls);

    const fragment = document.createDocumentFragment();

    // Update pagination UI
    const updatePaginationUI = (totalItems: number) => {
        const totalPages = Math.ceil(totalItems / itemsPerPage);
        const pageNavigation = tableContainer.querySelector('div:last-child div:last-child');
        if (!pageNavigation) return;

        pageNavigation.innerHTML = `
            <button ${currentPage === 1 ? 'disabled' : ''} onclick="window.changePage(${currentPage - 1})" style="padding: 5px 10px; border-radius: 4px; ${currentPage === 1 ? 'opacity: 0.5;' : ''}">Previous</button>
            <span>Page ${currentPage} of ${totalPages}</span>
            <button ${currentPage === totalPages ? 'disabled' : ''} onclick="window.changePage(${currentPage + 1})" style="padding: 5px 10px; border-radius: 4px; ${currentPage === totalPages ? 'opacity: 0.5;' : ''}">Next</button>
        `;
    };

    // Add event listener for items per page selector
    const itemsPerPageSelect = document.getElementById('itemsPerPageSelect') as HTMLSelectElement;
    if (itemsPerPageSelect) {
        itemsPerPageSelect.value = itemsPerPage.toString();
        itemsPerPageSelect.addEventListener('change', () => {
            itemsPerPage = parseInt(itemsPerPageSelect.value);
            currentPage = 1;
            renderControlsTable(window.mcmControls, configStandards, expanded).then(() => {
                // Reinitialize Tableordion after page change
                new Tableordion('.tableordion-row');
            });
        });
    }

    // Add page change function to window
    (window as any).changePage = (newPage: number) => {
        currentPage = newPage;
        renderControlsTable(window.mcmControls, configStandards, expanded).then(() => {
            // Reinitialize Tableordion after page change
            new Tableordion('.tableordion-row');
        });
    };

    if (Array.isArray(controls) && !configStandards) {
        const groupedControls = new Map();

        // Group controls by standard
        for (const control of controls) {
            const standard = control.standard;
            if (!groupedControls.has(standard)) {
                groupedControls.set(standard, []);
            }
            groupedControls.get(standard).push(control);
        }

        // Calculate total items and update pagination UI
        const totalItems = Array.from(groupedControls.values()).reduce((acc, controls) => acc + controls.length, 0);
        updatePaginationUI(totalItems);

        // Create a flattened array of all controls
        const allControls = Array.from(groupedControls.values()).flat();

        // Get controls for current page
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const pageControls = allControls.slice(startIndex, endIndex);

        // Group page controls by standard
        const pageGroupedControls = new Map();
        for (const control of pageControls) {
            const standard = control.standard;
            if (!pageGroupedControls.has(standard)) {
                pageGroupedControls.set(standard, []);
            }
            pageGroupedControls.get(standard).push(control);
        }

        // Sort page grouped controls by standard
        const sortedPageGroupedControls = new Map(
            Array.from(pageGroupedControls).sort(([a], [b]) =>
                a.localeCompare(b, undefined, { numeric: true })
            )
        );


        for (const [standardName, standardControls] of sortedPageGroupedControls) {
            {
                let index = 0;

                const configDiv = document.createElement('div');
                configDiv.classList.add('configurationStandard');

                // Set the standard name dynamically
                configDiv.innerHTML = `
            <div class="configurationStandard--header" style="cursor: pointer;">
                <div class="configStandard">
                    <div class="checkbox">
                        <label for="${standardName}-checkbox">
                            <input type="checkbox" name="optionsCheckboxes" value="${standardName}" id="${standardName}-checkbox" data-configid="${standardName}_controls">
                            <span class="deit-checkbox"></span>
                        </label>
                    </div>
                    <span class="configurationStandard--name">${standardName}</span>
                </div>
                <button class="configStandard-collapse">
                    <i class="fa-duotone fa-solid fa-chevron-down" style="color: rgba(0,0,0,0.32); font-size: 25px;"></i>
                </button>
            </div>
        `;

                const table = document.createElement('table');
                table.classList.add('tableordion');
                table.id = `configurationsTable`;

                // Start all tables collapsed
                table.classList.add('collapsed');
                table.style.height = '0px';
                table.style.display = 'none';

                index++;

                table.innerHTML = `
            <thead>
                <tr class="tableordion-header">
                    <th>Control ID</th>
                    <th style="width: 40rem;">Control Description</th>
                    <th>Owner</th>
                    <th>Assurance Tasks</th>
                    <th>Configuration Standards</th>
                    <th>Policies</th>
                    <th>Documents</th>
                    <th>Manage</th>
                </tr>
            </thead>
            <tbody class="tableordion-body"></tbody>
        `;

                const tbody = table.querySelector('tbody');

                standardControls.sort((a, b) => a.controlID.localeCompare(b.controlID));

                for (const control of standardControls) {
                    {
                        const row = document.createElement('tr');
                        row.classList.add('tableordion-row');
                        if (control.controlID === "DISP016") {
                            console.log("DISP016", control);
                        }
                        row.dataset.toggle = (expanded) ? 'collapse' : 'expand';

                        row.innerHTML = `
                <td>
                    <div style="display: flex; gap: 25px; align-items: center;">
                        <div class="checkbox">
                            <label for="${control.controlID}-checkbox">
                                <input type="checkbox" name="optionsCheckboxes" value="${control.controlID}" id="${control.controlID}-checkbox" data-configid="${standardName}_controls">
                                <span class="deit-checkbox"></span>
                            </label>
                        </div>
                        <span class="badge standard">${control.controlID}</span>
                    </div>
                </td>
                <td>${control?.controlDescription || 'N/A'}</td>
                <td>${control?.controlOwner || 'Unassigned'}</td>
                <td><span class="loading-placeholder">Loading...</span></td>
                <td><span class="loading-placeholder">Loading...</span></td>
                <td><span class="loading-placeholder">Loading...</span></td>
                <td style="width: 10%;"><span class="loading-placeholder">Loading...</span></td>
                <td class="action-buttons">
                    <button class="action-btn" onclick="window.individualOperations('${control.controlID}');" data-controlID="${control.controlID}">
                            <i class="fa-duotone fa-solid fa-pencil edit-icon" alt="Edit"></i>
                        </button>
                    <button class="action-btn collapse-toggle" data-toggle="${expanded ? 'collapse' : 'expand'}">
                            <i class="fa-duotone fa-solid fa-square-chevron-${expanded ? 'up' : 'down'}" style="color: ${expanded ? '#F6961D' : '#585555'};"></i>
                        </button>
                </td>
            `;

                        // Load the detailed content immediately
                        const loadDetailedContent = async () => {
                            try {
                                const [tasks, configStandards, policies, docs] = await Promise.all([
                                    renderArray(control?.tasks || [], null, "ASS", true, control.controlID, true),
                                    renderArray(control?.relatedConfigurationStandard || [], null, null, false, false, true),
                                    renderArray(control?.policies || [], null, "POL", false, false, true),
                                    renderArray(control?.supportingDocuments || [], null, "SUP", false, false, true)
                                ]);

                                // Update the cells with loaded content
                                const cells = row.querySelectorAll('td');
                                const [tasksCell, configCell, policiesCell, docsCell] = Array.from(cells).slice(3, 7);

                                if (tasksCell) (tasksCell as HTMLElement).innerHTML = tasks;
                                if (configCell) (configCell as HTMLElement).innerHTML = configStandards;
                                if (policiesCell) (policiesCell as HTMLElement).innerHTML = policies;
                                if (docsCell) (docsCell as HTMLElement).innerHTML = docs;
                            } catch (error) {
                                console.error('Error loading detailed content:', error);
                                const cells = row.querySelectorAll('td');
                                cells.forEach(cell => {
                                    const placeholder = cell.querySelector('.loading-placeholder');
                                    if (placeholder) {
                                        placeholder.textContent = 'Error loading content';
                                        (placeholder as HTMLElement).style.color = 'red';
                                    }
                                });
                            }
                        };

                        // Start loading the content immediately
                        loadDetailedContent();

                        // Add click handler for expanding/collapsing only
                        const collapseToggle = row;
                        if (collapseToggle) {
                            collapseToggle.addEventListener('click', async (e) => {
                                const button = e.currentTarget as HTMLButtonElement;
                                const isExpanding = button.getAttribute('data-toggle') === 'expand';

                                if (isExpanding) {
                                    // Keep track of the current display state
                                    const currentDisplay = (row as HTMLElement).style.display;

                                    // Use requestAnimationFrame to ensure smooth transition
                                    requestAnimationFrame(() => {
                                        // Ensure the row maintains its visibility
                                        (row as HTMLElement).style.display = currentDisplay;
                                    });
                                }
                            });
                        }

                        tbody.appendChild(row);

                        const contentRow = document.createElement('tr');
                        contentRow.classList.add('tableordion-content', 'hidden');
                        contentRow.dataset.issueId = control.controlID;
                        contentRow.style.display = 'none';

                        contentRow.innerHTML = `
    <td colspan="8">
        <div class="accordion-inner-content" style="display: flex; flex-wrap: wrap; gap: 48px; justify-content: space-evenly; align-items: flex-start; padding: 30px 0px;">
            <!-- Group: Assets -->
            <div style="display: flex; flex-direction: column; align-items: center; min-width: 250px; font-size: 13px !important;">
                <div style="font-size: 14px; font-weight: 550 !important; color: rgba(37, 43, 54, 0.95);">Assets</div>
                <p>${await renderArray(control?.assets || [], null, 'AST')}</p>
            </div>
            
            <!-- Group: Assurance Tasks -->
            <div style="display: flex; flex-direction: column; align-items: center; min-width: 250px; font-size: 13px !important;">
                <div style="font-size: 14px; font-weight: 550 !important; color: rgba(37, 43, 54, 0.95);">Assurance Tasks</div>
                <p>${await renderArray(control?.tasks || [], null, null, true, control.controlID)}</p>
            </div>
            
            <!-- Group: Configuration Standards -->
            <div style="display: flex; flex-direction: column; align-items: center; min-width: 250px; font-size: 13px !important;">
                <div style="font-size: 14px; font-weight: 550 !important; color: rgba(37, 43, 54, 0.95);">Configuration Standards</div>
                <p>${await renderArray(control?.relatedConfigurationStandard || [], null, null)}</p>
            </div>
            
            <!-- Group: Policies -->
            <div style="display: flex; flex-direction: column; align-items: center; min-width: 250px; font-size: 13px !important;">
                <div style="font-size: 14px; font-weight: 550 !important; color: rgba(37, 43, 54, 0.95);">Policies</div>
                <p>${await renderArray(control?.policies || [], null, "POL")}</p>
            </div>
            
            <!-- Group: Related Controls -->
            <div style="display: flex; flex-direction: column; align-items: center; min-width: 250px; font-size: 13px !important;">
                <div style="font-size: 14px; font-weight: 550 !important; color: rgba(37, 43, 54, 0.95);">Related Controls</div>
                <p>${await renderArray(control?.relatedControls || [], 'standard', null)}</p>
            </div>
            
            <!-- Group: Risks -->
            <div style="display: flex; flex-direction: column; align-items: center; min-width: 250px; font-size: 13px !important;">
                <div style="font-size: 14px; font-weight: 550 !important; color: rgba(37, 43, 54, 0.95);">Risks</div>
                <p>${await renderArray(control?.risks || [], null, 'RSK')}</p>
            </div>
            
            <!-- Group: Supporting Documents -->
            <div style="display: flex; flex-direction: column; align-items: center; min-width: 250px; font-size: 13px !important;">
                <div style="font-size: 14px; font-weight: 550 !important; color: rgba(37, 43, 54, 0.95);">Supporting Documents</div>
                <p>${await renderArray(control?.supportingDocuments || [], null, 'SUP')}</p>
            </div>
            
            <!-- Group: Legal Obligations -->
            <div style="display: flex; flex-direction: column; align-items: center; min-width: 250px; font-size: 13px !important;">
                <div style="font-size: 14px; font-weight: 550 !important; color: rgba(37, 43, 54, 0.95);">Legal Obligations</div>
                <p>${await renderArray(control?.legalObligations || [], null, 'LEO')}</p>
            </div>
            
            <!-- Group: ISMS Entries -->
            <div style="display: flex; flex-direction: column; align-items: center; min-width: 250px; font-size: 13px !important;">
                <div style="font-size: 14px; font-weight: 550 !important; color: rgba(37, 43, 54, 0.95);">ISMS Entries</div>
                <p>${await renderArray(control?.ismsEntries || [], null)}</p>
            </div>
            
            <!-- Group: ISMS Objectives -->
            <div style="display: flex; flex-direction: column; align-items: center; min-width: 250px; font-size: 13px !important;">
                <div style="font-size: 14px; font-weight: 550 !important; color: rgba(37, 43, 54, 0.95);">ISMS Objectives</div>
                <p>${await renderArray(control?.ismsObjectives || [], null, "OBJ")}</p>
            </div>
            
            <!-- Group: Implementation Status -->
            <div style="display: flex; flex-direction: column; align-items: center; min-width: 250px; font-size: 13px !important;">
                <div style="font-size: 14px; font-weight: 550 !important; color: rgba(37, 43, 54, 0.95);">Implementation Status</div>
                <p><div>${control?.controlApplicable ? 'Implemented' : 'Not Implemented'}</div></p>
            </div>
        </div>
    </td>
`;

                        tbody.appendChild(contentRow);
                    }
                }

                configDiv.appendChild(table);
                fragment.appendChild(configDiv);
            }
        }
    } else if (typeof controls === 'object') {
        Object.values(controls).sort((a, b) => a.configurationStandard.localeCompare(b.configurationStandard));
        let index = 0;
        for (const configStandard of Object.values(controls)) {
            const configDiv = document.createElement('div');
            configDiv.classList.add('configurationStandard');

            configDiv.innerHTML = `
                <div class="configurationStandard--header" style="cursor: pointer;">
                    <div class="configStandard">
                        <div class="checkbox">
                            <label for="${configStandard.configurationStandard.trim() === "" ? "unassigned" : sanitise(configStandard.configurationStandard.toLowerCase().replaceAll(' ', '_'))}-checkbox">
                                <input type="checkbox" name="optionsCheckboxes" value="${configStandard.configurationStandard.trim() === "" ? "Unassigned" : configStandard.configurationStandard}" id="${configStandard.configurationStandard.trim() === "" ? "unassigned" : sanitise(configStandard.configurationStandard.toLowerCase().replaceAll(' ', '_'))}-checkbox" data-configid="${configStandard.configurationStandard.trim() === "" ? "unassigned" : sanitise(configStandard.configurationStandard.toLowerCase().replaceAll(' ', '_'))}_controls">
                                <span class="deit-checkbox"></span>
                            </label>
                        </div>
                        <span class="configurationStandard--name">${configStandard.configurationStandard.trim() === "" ? "Unassigned" : configStandard.configurationStandard}</span>
                    </div>
                    <button class="configStandard-collapse">
                        <i class="fa-duotone fa-solid fa-chevron-down" style="color: rgba(0,0,0,0.32); font-size: 25px;"></i>
                    </button>
                </div>
            `;

            const table = document.createElement('table');
            table.classList.add('tableordion');
            table.id = 'configurationsTable';

            if (index !== 0) {
                table.classList.add('collapsed');
                table.style.height = '0px';
                table.style.display = 'none';
            }

            index++;

            table.innerHTML = `
                <thead>
                    <tr class="tableordion-header">
                        <th>Standard</th>
                        <th>Section</th>
                        <th>Topic</th>
                        <th>Control ID</th>
                        <th>Control Description</th>
                        <th>Control Owner</th>
                        <th>Manage</th>
                    </tr>
                </thead>
                <tbody class="tableordion-body"></tbody>
            `;

            const tbody = table.querySelector('tbody');

            for (const control of configStandard.controls) {
                {
                    const row = document.createElement('tr');
                    row.classList.add('tableordion-row');

                    row.innerHTML = `
                        <td>
                            <div style="display: flex; gap: 25px; align-items: center;">
                                <div class="checkbox">
                                    <label for="control-${control.controlID}-checkbox">
                                        <input type="checkbox" name="optionsCheckboxes" value="${control.controlID}" id="control-${control.controlID}-checkbox" data-configid="${configStandard.configurationStandard.trim() === "" ? "unassigned" : sanitise(configStandard.configurationStandard.toLowerCase().replaceAll(' ', '_'))}_controls">
                                        <span class="deit-checkbox"></span>
                                    </label>
                                </div>
                                <span class="badge standard">${control.standard}</span>
                            </div>
                        </td>
                        <td>${control.section}</td>
                        <td>${control.topic}</td>
                        <td>${control.controlID}</td>
                        <td>${truncateTo15Words(control.controlDescription)}</td>
                        <td>${control.controlOwner}</td>
                        <td class="action-buttons">
                            <button class="action-btn" onclick="window.individualOperations('${control.controlID}');" data-controlID="${control.controlID}">
                                <i class="fa-duotone fa-solid fa-pencil edit-icon" alt="Edit"></i>
                            </button>
                            <button class="action-btn collapse-toggle" data-toggle="${expanded ? 'collapse' : 'expand'}">
                                <i class="fa-duotone fa-solid fa-square-chevron-${expanded ? 'up' : 'down'}" style="color: ${expanded ? '#F6961D' : '#585555'};"></i>
                            </button>
                        </td>
                    `;

                    tbody.appendChild(row);

                    const contentRow = document.createElement('tr');
                    contentRow.classList.add('tableordion-content', expanded ? 'accordion-content-active' : 'hidden');
                    contentRow.dataset.issueId = control.controlID;
                    contentRow.style.display = expanded ? 'table-row' : 'none';

                    contentRow.innerHTML = `
                    <td colspan="7">
                        <div class="accordion-inner-content">
                            <table>
                                <thead>
                                    <tr class="tableordion-header">
                                        <th>Control Description</th>
                                        <th>Assets</th>
                                        <th>Supporting Documents</th>
                                        <th>Assurance Tasks</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td style="width: 30%;">${control.controlDescription}</td>
                                        <td>${await renderArray(control?.assets || [], null, 'AST')}</td>
                                        <td>${await renderArray(control?.supportingDocuments || [], null, 'SUP')}</td>
                                        <td>${await renderArray(control?.tasks || [], null, 'ASS')}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </td>
                `;

                    tbody.appendChild(contentRow);
                }
            }

            configDiv.appendChild(table);
            fragment.appendChild(configDiv);
        }
    }

    tableContainer.appendChild(fragment);

    // Initialize Tableordion for the initial render
    if (window.location.pathname === "/configurationStandards.html" || window.location.pathname === "/configurationStandards") new Tableordion('.tableordion-row');

    document.querySelectorAll('.configurationStandard--header input[type="checkbox"]').forEach(checkbox => {
        const relatedCheckboxes = document.querySelectorAll(`.tableordion-row input[type="checkbox"][data-configID="${(checkbox as HTMLElement).dataset.configid}"]`);

        checkbox.addEventListener('change', function () {
            try {
                const isChecked = this.checked;

                relatedCheckboxes.forEach(box => {
                    (box as HTMLInputElement).checked = isChecked;

                    // Ensure controlsToUpdate is initialized
                    if (!(window as any).mcmControlsToUpdate) (window as any).mcmControlsToUpdate = [];

                    const controlID = box.id.replaceAll('control-', '').replaceAll('-checkbox', '');

                    if (isChecked) {
                        // Add control ID if not already in the array
                        if (!(window as any).mcmControlsToUpdate.includes(controlID)) {
                            (window as any).mcmControlsToUpdate.push(controlID);
                        }
                    } else {
                        // Remove control ID if it exists in the array
                        const index = (window as any).mcmControlsToUpdate.indexOf(controlID);
                        if (index > -1) {
                            (window as any).mcmControlsToUpdate.splice(index, 1);
                        }
                    }
                });

                updateFloatingBarAndCount();
            } catch (error) {
                console.error('Error in checkbox change event:', error);
            }
        });
    });

    document.querySelectorAll('#configurationsTable .tableordion-row input[type="checkbox"]').forEach(checkbox => {
        checkbox.addEventListener('change', function () {
            try {
                const isChecked = this.checked;

                // Ensure controlsToUpdate is initialized
                if (!(window as any).mcmControlsToUpdate) (window as any).mcmControlsToUpdate = [];

                const controlID = this.id.replaceAll('control-', '').replaceAll('-checkbox', '');

                if (isChecked) {
                    // Add control ID if not already in the array
                    if (!(window as any).mcmControlsToUpdate.includes(controlID)) {
                        (window as any).mcmControlsToUpdate.push(controlID);
                    }
                } else {
                    // Remove control ID if it exists in the array
                    const index = (window as any).mcmControlsToUpdate.indexOf(controlID);
                    if (index > -1) {
                        (window as any).mcmControlsToUpdate.splice(index, 1);
                    }
                }

                updateFloatingBarAndCount();
            } catch (error) {
                console.error('Error in individual checkbox change event:', error);
            }
        });
    });

    // Function to update the floating bar visibility and selected count
    function updateFloatingBarAndCount() {
        const floatingBar = document.getElementById('floatingBar');
        const totalSelected = document.querySelectorAll('.tableordion-row input[type="checkbox"]:checked').length;
        const countElements = document.querySelectorAll('.selectedCount .count');

        if (countElements.length >= 1) {
            countElements.forEach(countElement => {
                countElement.textContent = totalSelected.toString();
            });
        }

        if (totalSelected > 0) {
            floatingBar.classList.remove('hidden');
        } else {
            floatingBar.classList.add('hidden');
        }
    }

    document.querySelectorAll('.configurationStandard--header').forEach(collapseButton => {
        collapseButton.addEventListener('click', async () => {
            const standardContainer = collapseButton.parentElement;
            const table = standardContainer.querySelector('#configurationsTable') as HTMLElement;
            const icon = collapseButton.querySelector('.fa-chevron-up, .fa-chevron-down') as HTMLElement;
            const standardName = standardContainer.querySelector('.configurationStandard--name')?.textContent;

            const isCollapsed = table.classList.contains('collapsed');

            if (isCollapsed && standardName) {
                // First show the table
                table.classList.remove('collapsed');
                table.style.display = 'table';

                // Load controls for this standard if not already loaded
                if (!loadedStandards.has(standardName)) {
                    try {
                        // Show loading state in each cell that needs to load
                        const rows = table.querySelectorAll('tbody tr');
                        rows.forEach(row => {
                            const cells = row.querySelectorAll('td');
                            cells.forEach((cell, index) => {
                                // Only update cells that need to load (tasks, config standards, policies, docs)
                                if (index >= 3 && index <= 6) {
                                    const placeholder = cell.querySelector('.loading-placeholder');
                                    if (placeholder instanceof HTMLElement) {
                                        placeholder.textContent = 'Loading...';
                                    }
                                }
                            });
                        });

                        // Fetch and update the content
                        const standardControls = await fetchControlsForStandard(standardName);
                        loadedStandards.add(standardName);

                        // Update each row with its content
                        rows.forEach(async (row) => {
                            const cells = row.querySelectorAll('td');
                            const checkbox = row.querySelector('input[type="checkbox"]') as HTMLInputElement;
                            const controlId = checkbox?.value;
                            if (controlId) {
                                const control = standardControls.find(c => c.controlID === controlId);
                                if (control) {
                                    const tasks = await renderArray(control?.tasks || [], null, "ASS", true, control.controlID, true);
                                    const configStandards = await renderArray(control?.relatedConfigurationStandard || [], null, null, false, false, true);
                                    const policies = await renderArray(control?.policies || [], null, "POL", false, false, true);
                                    const docs = await renderArray(control?.supportingDocuments || [], null, "SUP", false, false, true);

                                    // Update cells with content
                                    const [, , , tasksCell, configCell, policiesCell, docsCell] = Array.from(cells) as HTMLTableCellElement[];
                                    if (tasksCell) tasksCell.innerHTML = tasks;
                                    if (configCell) configCell.innerHTML = configStandards;
                                    if (policiesCell) policiesCell.innerHTML = policies;
                                    if (docsCell) docsCell.innerHTML = docs;
                                }
                            }
                        });
                    } catch (error) {
                        console.error('Error loading controls:', error);
                        const rows = table.querySelectorAll('tbody tr');
                        rows.forEach(row => {
                            const cells = row.querySelectorAll('td');
                            cells.forEach((cell, index) => {
                                if (index >= 3 && index <= 6) {
                                    const placeholder = cell.querySelector('.loading-placeholder');
                                    if (placeholder instanceof HTMLElement) {
                                        placeholder.textContent = 'Error loading content';
                                        (placeholder as HTMLElement).style.color = 'red';
                                    }
                                }
                            });
                        });
                    }
                }

                // Animate the table opening after content is loaded
                const tableHeight = table.offsetHeight;
                table.style.height = '0px';
                anime({
                    targets: table,
                    height: [0, `${tableHeight}px`],
                    duration: 500,
                    easing: 'easeOutQuad',
                    complete: function () {
                        table.style.height = 'auto';
                    }
                });

                // Animate icon
                anime({
                    targets: icon,
                    opacity: [1, 0],
                    duration: 250,
                    easing: 'easeOutQuad',
                    complete: () => {
                        icon.classList.remove('fa-chevron-down');
                        icon.classList.add('fa-chevron-up');
                        anime({
                            targets: icon,
                            opacity: [0, 1],
                            duration: 250,
                            easing: 'easeInQuad'
                        });
                    }
                });

                // Animate opening
                const fullHeight = table.offsetHeight + 'px';
                table.style.height = '0px';
                anime({
                    targets: table,
                    height: [0, fullHeight],
                    duration: 500,
                    easing: 'easeOutQuad',
                    complete: function () {
                        table.style.height = 'auto';
                    }
                });

                // Animate icon
                anime({
                    targets: icon,
                    opacity: [1, 0],
                    duration: 250,
                    easing: 'easeOutQuad',
                    complete: () => {
                        icon.classList.remove('fa-chevron-down');
                        icon.classList.add('fa-chevron-up');
                        anime({
                            targets: icon,
                            opacity: [0, 1],
                            duration: 250,
                            easing: 'easeInQuad'
                        });
                    }
                });
            } else {
                var currentHeight = table.offsetHeight + 'px';
                table.style.height = currentHeight;
                table.style.display = 'block';
                anime({
                    targets: table,
                    height: [currentHeight, 0],
                    duration: 500,
                    easing: 'easeInQuad',
                    complete: function () {
                        table.style.display = 'none';
                        table.classList.add('collapsed');
                    }
                });
                anime({
                    targets: icon,
                    opacity: [1, 0],
                    duration: 250,
                    easing: 'easeOutQuad',
                    complete: () => {
                        icon.classList.remove('fa-chevron-up');
                        icon.classList.add('fa-chevron-down');
                        anime({
                            targets: icon,
                            opacity: [0, 1],
                            duration: 250,
                            easing: 'easeInQuad'
                        });
                    }
                });
            }
        });
    });
    hideLoadingScreen(); // Hide loading screen after rendering
}

let isSavingControls = false; // Global flag to track if saving is in progress

export function setupControlsListener(useISM: boolean = true): void {
    let configurationStandards: { [key: string]: ConfigurationStandard } = {};
    waitForCompanyID().then(companyID => {
        if (useISM) {
            const collectionPath = `/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsSoa/ism`;
            const collectionRef = db.collection(collectionPath);

            collectionRef.onSnapshot(async (snapshot) => {
                if (isSavingControls) return; // Skip updates if saving is in progress

                let controls: ControlData[] = [];

                const controlPromises = snapshot.docs.map(async (doc) => {
                    const data = doc.data();
                    const controlOwnerName = await nameFromUID(data.controlOwner) || 'N/A';
                    const controlData: ControlData = {
                        standard: 'ISM',
                        section: data.section || 'N/A',
                        topic: data.topic || 'N/A',
                        controlID: data.controlID || 'N/A',
                        controlDescription: data.description || 'N/A',
                        controlOwner: controlOwnerName,
                        assets: data.assets || [],
                        tasks: data.tasks || [],
                        supportingDocuments: data.supportingDocuments || [],
                    };

                    if (data.hasOwnProperty('relatedConfigurationStandard') && data.relatedConfigurationStandard) {
                        let relatedStandards = Array.isArray(data.relatedConfigurationStandard)
                            ? data.relatedConfigurationStandard.map((standard) => standard.trim()).filter(Boolean)
                            : typeof data.relatedConfigurationStandard === 'string'
                                ? data.relatedConfigurationStandard.split(',').map((standard) => standard.trim()).filter(Boolean)
                                : [];

                        // Ensure empty arrays are assigned to "Unassigned"
                        if (relatedStandards.length === 0) {
                            relatedStandards = ["Unassigned"];
                        }

                        relatedStandards.forEach((standard) => {
                            if (!configurationStandards.hasOwnProperty(standard)) {
                                configurationStandards[standard] = {
                                    configurationStandard: standard,
                                    controls: [],
                                };
                            }
                            configurationStandards[standard].controls.push(controlData);
                        });
                    }
                    controls.push(controlData);
                });

                await Promise.all(controlPromises);
                (window as any).controls = controls;
                renderControlsTable(configurationStandards, true);
            }, error => {
                console.error('Error with onSnapshot listener:', error);
                createToast({
                    message: 'Real-time updates failed.',
                    customStyles: {
                        backgroundColor: '#D8090F',
                        color: '#ffffff'
                    }
                });
            });
        }
    });
}

/**
 * The function `getExistingControl` retrieves control data based on a given control ID, handling cases
 * where the control is not found.
 * @param {string} controlID - The `controlID` parameter is a string that represents the unique
 * identifier of a control. This function `getExistingControl` is an asynchronous function that
 * retrieves control data based on the provided `controlID`. It fetches control data, flattens it into
 * a single array, resolves control promises, and
 * @returns The function `getExistingControl` returns a Promise that resolves to either the full
 * control object if found based on the provided `controlID`, or `null` if the control is not found or
 * if there is an error during the retrieval process.
 */
async function getExistingControl(controlID: string): Promise<any | null> {
    try {
        // check customer ismssoa first before returning default control
        const companyID = await waitForCompanyID();
        const ISMRef = db.collection(`/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsSoa/ism`).doc(controlID);
        const ISMSRef = db.doc(`/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsSoa`);
        const ISMRefData = await ISMRef.get();
        if (ISMRefData.exists) {
            return ISMRefData.data();
        }

        const ISMSRefData = await ISMSRef.get();
        if (ISMSRefData.exists) {
            const ISMSData = ISMSRefData.data();
            if (ISMSData.hasOwnProperty(controlID)) {
                return ISMSData[controlID];
            }
        }

        // Fetch the controls data
        const controlsData = await (window as any).ControlLibrary.Control.Get();

        // Flatten controls into a single array
        const controlPromises = Object.keys(controlsData).flatMap(standard =>
            controlsData[standard].flatMap(standardData =>
                standardData.controls.map(async (control) => ({
                    id: control.controlID,
                    name: control.controlName || control.description || "Unnamed Control",
                    standard: standardData.standardID,
                    isISM: standardData.standardID === "ISM" || standardData.standardID === "Essential 8",
                    ...control
                }))
            )
        );

        // Resolve all control promises
        const controlData = await Promise.all(controlPromises);

        // Find the control by ID
        let control = controlData.find(c => c.id === controlID);

        // Handle case where control is not found
        if (!control) {
            console.error(`Failed to find control with ID ${controlID} - Assuming Custom Control`);
            control = (window as any).quickLookup[controlID];
            if (!control) {
                console.error('Control doesn\'t exist anywhere', controlID);
                return null;
            }
        }

        // Return the full control object
        return control;
    } catch (error) {
        console.error("Error retrieving control:", error);
        return null;
    }
}

function mergeArraysInControl(existing: Record<string, any> | null | undefined, incoming: Record<string, any>): Record<string, any> {
    // Ensure `existing` is an object to avoid runtime errors
    const safeExisting = existing || {};

    const result: Record<string, any> = { ...safeExisting };

    // Define array fields that can be unmapped
    const arrayFields = [
        'assets', 'tasks', 'supportingDocuments', 'policies',
        'relatedConfigurationStandard', 'risks', 'relatedControls',
        'ismsEntries', 'ismsObjectives', 'legalObligations', 'suppliers'
    ];

    for (const key in incoming) {
        const incomingValue = incoming[key];
        const existingValue = safeExisting[key];

        if (arrayFields.includes(key)) {
            // For array fields, if the key exists in incoming, use its value (even if undefined)
            // This allows for explicitly removing all items by passing an empty array
            result[key] = Array.isArray(incomingValue) ? [...incomingValue] : [];
        } else if (incomingValue !== null && incomingValue !== undefined) {
            // For non-array fields, overwrite if incoming has a value
            result[key] = incomingValue;
        } else if (existingValue !== null && existingValue !== undefined) {
            // For non-array fields, retain existing value if incoming is null/undefined
            result[key] = existingValue;
        } else {
            // Handle cases where both are null/undefined
            result[key] = null;
        }
    }

    return result;
}

/**
 * Saves data to a single control.
 * @param controlID The ID of the control to save to.
 * @param options The data to save to the control.
 * @param useISM Boolean to determine whether to use ISM or another path.
 */
export async function saveControl(controlID: string, options: Record<string, any>, useISM: boolean = true, dontRunRelated = false): Promise<void> {
    try {
        const companyID = await waitForCompanyID();

        let existingControl = await getExistingControl(controlID);

        const mergedControl = mergeArraysInControl(existingControl, options);

        let isISM = useISM;
        if (mergedControl?.isISM !== undefined) {
            isISM = mergedControl?.isISM;
        }

        const collectionPath = isISM
            ? `/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsSoa/ism`
            : `/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsSoa`;
        const docRef = isISM
            ? db.collection(collectionPath).doc(controlID)
            : db.doc(`${collectionPath}`);

        if (isISM) {
            mergedControl['standard'] = "ISM";
        }

        (window as any).quickLookup[controlID] = mergedControl;

        console.log('Saving Control', {
            controlID,
            options,
            mergedControl,
            existingControl
        })

        isISM ? await docRef.set(mergedControl, { merge: true }) : await docRef.set({ [controlID]: { ...mergedControl } }, { merge: true });

        if (!dontRunRelated) {
            // Get the existing control to compare what was removed
            const existingControl = await getExistingControl(controlID);

            // Handle assets
            if (options.hasOwnProperty('assets')) {
                // Get items that were removed (in existing but not in new options)
                const removedAssets = existingControl?.assets?.filter(id => !options.assets.includes(id)) || [];
                // Remove this control from removed items
                for (const assetId of removedAssets) {
                    try {
                        const asset = await (window as any).AssetRegister.Asset.Get(assetId);
                        const assetData = await asset.data;
                        const updatedControls = (assetData.relatedControls || []).filter(id => id !== controlID);
                        await (window as any).AssetRegister.Asset.Edit(assetId, { relatedControls: updatedControls });
                        console.log(`Removed ${controlID} from asset ${assetId}`);
                    } catch (error) {
                        console.error(`Failed to remove ${controlID} from asset ${assetId}:`, error);
                    }
                }
                // Add this control to new items
                for (const assetId of options.assets) {
                    if (!existingControl?.assets?.includes(assetId)) {
                        try {
                            const asset = await (window as any).AssetRegister.Asset.Get(assetId);
                            const assetData = await asset.data;
                            const existingControls = assetData.relatedControls || [];
                            if (!existingControls.includes(controlID)) {
                                existingControls.push(controlID);
                                await (window as any).AssetRegister.Asset.Edit(assetId, { relatedControls: existingControls });
                                console.log(`Added ${controlID} to asset ${assetId}`);
                            }
                        } catch (error) {
                            console.error(`Failed to add ${controlID} to asset ${assetId}:`, error);
                        }
                    }
                }
            }

            // Handle suppliers
            if (options.hasOwnProperty('suppliers')) {
                const removedSuppliers = existingControl?.suppliers?.filter(id => !options.suppliers.includes(id)) || [];
                for (const supplierId of removedSuppliers) {
                    try {
                        const supplier = await (window as any).AssetRegister.Asset.Get(supplierId);
                        const supplierData = await supplier.data;
                        const updatedControls = (supplierData.relatedControls || []).filter(id => id !== controlID);
                        await (window as any).AssetRegister.Asset.Edit(supplierId, { relatedControls: updatedControls });
                        console.log(`Removed ${controlID} from supplier ${supplierId}`);
                    } catch (error) {
                        console.error(`Failed to remove ${controlID} from supplier ${supplierId}:`, error);
                    }
                }
                for (const supplierId of options.suppliers) {
                    if (!existingControl?.suppliers?.includes(supplierId)) {
                        try {
                            const supplier = await (window as any).AssetRegister.Asset.Get(supplierId);
                            const supplierData = await supplier.data;
                            const existingControls = supplierData.relatedControls || [];
                            if (!existingControls.includes(controlID)) {
                                existingControls.push(controlID);
                                await (window as any).AssetRegister.Asset.Edit(supplierId, { relatedControls: existingControls });
                                console.log(`Added ${controlID} to supplier ${supplierId}`);
                            }
                        } catch (error) {
                            console.error(`Failed to add ${controlID} to supplier ${supplierId}:`, error);
                        }
                    }
                }
            }

            // Handle tasks
            if (options.hasOwnProperty('tasks')) {
                const removedTasks = existingControl?.tasks?.filter(id => !options.tasks.includes(id)) || [];
                for (const taskId of removedTasks) {
                    try {
                        const task = await (window as any).AssuranceLibrary.Task.Get(taskId);
                        const taskData = await task.data;
                        const updatedControls = (taskData.relatedControls || []).filter(id => id !== controlID);
                        await (window as any).AssuranceLibrary.Task.Edit(taskId, { relatedControls: updatedControls });
                        console.log(`Removed ${controlID} from task ${taskId}`);
                    } catch (error) {
                        console.error(`Failed to remove ${controlID} from task ${taskId}:`, error);
                    }
                }
                for (const taskId of options.tasks) {
                    if (!existingControl?.tasks?.includes(taskId)) {
                        try {
                            const task = await (window as any).AssuranceLibrary.Task.Get(taskId);
                            const taskData = await task.data;
                            const existingControls = taskData.relatedControls || [];
                            if (!existingControls.includes(controlID)) {
                                existingControls.push(controlID);
                                await (window as any).AssuranceLibrary.Task.Edit(taskId, { relatedControls: existingControls });
                                console.log(`Added ${controlID} to task ${taskId}`);
                            }
                        } catch (error) {
                            console.error(`Failed to add ${controlID} to task ${taskId}:`, error);
                        }
                    }
                }
            }

            // Handle supporting documents
            if (options.hasOwnProperty('supportingDocuments')) {
                const removedDocs = existingControl?.supportingDocuments?.filter(id => !options.supportingDocuments.includes(id)) || [];
                for (const docId of removedDocs) {
                    try {
                        const doc = await (window as any).DocumentLibrary.Doc.Get(docId);
                        const docData = await doc.data;
                        const updatedControls = (docData.relatedControls || []).filter(id => id !== controlID);
                        await (window as any).DocumentLibrary.Doc.Edit(docId, { relatedControls: updatedControls });
                        console.log(`Removed ${controlID} from document ${docId}`);
                    } catch (error) {
                        console.error(`Failed to remove ${controlID} from document ${docId}:`, error);
                    }
                }
                for (const docId of options.supportingDocuments) {
                    if (!existingControl?.supportingDocuments?.includes(docId)) {
                        try {
                            const doc = await (window as any).DocumentLibrary.Doc.Get(docId);
                            const docData = await doc.data;
                            const existingControls = docData.relatedControls || [];
                            if (!existingControls.includes(controlID)) {
                                existingControls.push(controlID);
                                await (window as any).DocumentLibrary.Doc.Edit(docId, { relatedControls: existingControls });
                                console.log(`Added ${controlID} to document ${docId}`);
                            }
                        } catch (error) {
                            console.error(`Failed to add ${controlID} to document ${docId}:`, error);
                        }
                    }
                }
            }

            // Handle policies
            if (options.hasOwnProperty('policies')) {
                const removedPolicies = existingControl?.policies?.filter(id => !options.policies.includes(id)) || [];
                for (const policyId of removedPolicies) {
                    try {
                        const policy = await (window as any).PolicyLibrary.Policy.Get(policyId);
                        const policyData = await policy.data;
                        const updatedControls = (policyData.relatedControls || []).filter(id => id !== controlID);
                        await (window as any).PolicyLibrary.Policy.Edit(policyId, { relatedControls: updatedControls });
                        console.log(`Removed ${controlID} from policy ${policyId}`);
                    } catch (error) {
                        console.error(`Failed to remove ${controlID} from policy ${policyId}:`, error);
                    }
                }
                for (const policyId of options.policies) {
                    if (!existingControl?.policies?.includes(policyId)) {
                        try {
                            const policy = await (window as any).PolicyLibrary.Policy.Get(policyId);
                            const policyData = await policy.data;
                            const existingControls = policyData.relatedControls || [];
                            if (!existingControls.includes(controlID)) {
                                existingControls.push(controlID);
                                await (window as any).PolicyLibrary.Policy.Edit(policyId, { relatedControls: existingControls });
                                console.log(`Added ${controlID} to policy ${policyId}`);
                            }
                        } catch (error) {
                            console.error(`Failed to add ${controlID} to policy ${policyId}:`, error);
                        }
                    }
                }
            }

            // Handle related controls
            if (options.hasOwnProperty('relatedControls')) {
                // Get items that were removed (in existing but not in new options)
                const removedControls = existingControl?.relatedControls?.filter(id => !options.relatedControls.includes(id)) || [];

                // Remove this control from removed items
                for (const relatedControlId of removedControls) {
                    try {
                        // Get the control data
                        const controlsData = await (window as any).ControlLibrary.Control.Get();
                        const controlPromises = Object.keys(controlsData).flatMap(standard => {
                            return controlsData[standard].flatMap(standardData => {
                                return standardData.controls.map(async (control) => ({
                                    id: control.controlID,
                                    name: control.controlName || control.description || "Unnamed Control",
                                    standard: standardData.standardID,
                                    isISM: standardData.standardID === "ISM" || standardData.standardID === "Essential 8" ? true : false,
                                    ...control
                                }));
                            });
                        });

                        const controlData = await Promise.all(controlPromises);
                        let control = (window as any).quickLookup[relatedControlId];
                        if (!control) {
                            control = controlData.find(c => c.id === relatedControlId);
                        }

                        if (control) {
                            const updatedControls = (control.relatedControls || []).filter(id => id !== controlID);
                            await saveControl(relatedControlId, { relatedControls: updatedControls }, (control?.isISM !== undefined ? control.isISM : false), true);
                            console.log(`Removed ${controlID} from control ${relatedControlId}`);
                        }
                    } catch (error) {
                        console.error(`Failed to remove ${controlID} from control ${relatedControlId}:`, error);
                    }
                }

                // Add this control to new items
                for (const relatedControlId of options.relatedControls) {
                    if (!existingControl?.relatedControls?.includes(relatedControlId)) {
                        try {
                            // Get the control data
                            const controlsData = await (window as any).ControlLibrary.Control.Get();
                            const controlPromises = Object.keys(controlsData).flatMap(standard => {
                                return controlsData[standard].flatMap(standardData => {
                                    return standardData.controls.map(async (control) => ({
                                        id: control.controlID,
                                        name: control.controlName || control.description || "Unnamed Control",
                                        standard: standardData.standardID,
                                        isISM: standardData.standardID === "ISM" || standardData.standardID === "Essential 8" ? true : false,
                                        ...control
                                    }));
                                });
                            });

                            const controlData = await Promise.all(controlPromises);
                            let control = (window as any).quickLookup[relatedControlId];
                            if (!control) {
                                control = controlData.find(c => c.id === relatedControlId);
                            }

                            if (control) {
                                const existingControls = control.relatedControls || [];
                                if (!existingControls.includes(controlID)) {
                                    existingControls.push(controlID);
                                    await saveControl(relatedControlId, { relatedControls: existingControls }, (control?.isISM !== undefined ? control.isISM : false), true);
                                    console.log(`Added ${controlID} to control ${relatedControlId}`);
                                }
                            }
                        } catch (error) {
                            console.error(`Failed to add ${controlID} to control ${relatedControlId}:`, error);
                        }
                    }
                }
            }

            // Handle risks
            if (options.hasOwnProperty('risks')) {
                const removedRisks = existingControl?.risks?.filter(id => !options.risks.includes(id)) || [];
                for (const riskId of removedRisks) {
                    try {
                        const risk = await (window as any).RiskRegister.Risk.Get(riskId);
                        const riskData = await risk.data;
                        const updatedControls = (riskData.relatedControls || []).filter(id => id !== controlID);
                        await (window as any).RiskRegister.Risk.Edit(riskId, { relatedControls: updatedControls });
                        console.log(`Removed ${controlID} from risk ${riskId}`);
                    } catch (error) {
                        console.error(`Failed to remove ${controlID} from risk ${riskId}:`, error);
                    }
                }
                for (const riskId of options.risks) {
                    if (!existingControl?.risks?.includes(riskId)) {
                        try {
                            const risk = await (window as any).RiskRegister.Risk.Get(riskId);
                            const riskData = await risk.data;
                            const existingControls = riskData.relatedControls || [];
                            if (!existingControls.includes(controlID)) {
                                existingControls.push(controlID);
                                await (window as any).RiskRegister.Risk.Edit(riskId, { relatedControls: existingControls });
                                console.log(`Added ${controlID} to risk ${riskId}`);
                            }
                        } catch (error) {
                            console.error(`Failed to add ${controlID} to risk ${riskId}:`, error);
                        }
                    }
                }
            }

            // Handle ISMS objectives
            if (options.hasOwnProperty('ismsObjectives')) {
                const removedObjectives = existingControl?.ismsObjectives?.filter(id => !options.ismsObjectives.includes(id)) || [];
                for (const objectiveId of removedObjectives) {
                    try {
                        const objective = await (window as any).ObjectivesRegister.Entry.Get(objectiveId);
                        const objectiveData = await objective.data;
                        const updatedControls = (objectiveData.relatedControls || []).filter(id => id !== controlID);
                        await (window as any).ObjectivesRegister.Entry.Edit(objectiveId, { relatedControls: updatedControls });
                        console.log(`Removed ${controlID} from objective ${objectiveId}`);
                    } catch (error) {
                        console.error(`Failed to remove ${controlID} from objective ${objectiveId}:`, error);
                    }
                }
                for (const objectiveId of options.ismsObjectives) {
                    if (!existingControl?.ismsObjectives?.includes(objectiveId)) {
                        try {
                            const objective = await (window as any).ObjectivesRegister.Entry.Get(objectiveId);
                            const objectiveData = await objective.data;
                            const existingControls = objectiveData.relatedControls || [];
                            if (!existingControls.includes(controlID)) {
                                existingControls.push(controlID);
                                await (window as any).ObjectivesRegister.Entry.Edit(objectiveId, { relatedControls: existingControls });
                                console.log(`Added ${controlID} to objective ${objectiveId}`);
                            }
                        } catch (error) {
                            console.error(`Failed to add ${controlID} to objective ${objectiveId}:`, error);
                        }
                    }
                }
            }

            // Handle legal obligations
            if (options.hasOwnProperty('legalObligations')) {
                const removedObligations = existingControl?.legalObligations?.filter(id => !options.legalObligations.includes(id)) || [];
                for (const obligationId of removedObligations) {
                    try {
                        const obligation = await (window as any).LegalObligationLibrary.LegalObligation.Get(obligationId);
                        const obligationData = await obligation.data;
                        const updatedControls = (obligationData.relatedControls || []).filter(id => id !== controlID);
                        await (window as any).LegalObligationLibrary.LegalObligation.Edit(obligationId, { relatedControls: updatedControls });
                        console.log(`Removed ${controlID} from obligation ${obligationId}`);
                    } catch (error) {
                        console.error(`Failed to remove ${controlID} from obligation ${obligationId}:`, error);
                    }
                }
                for (const obligationId of options.legalObligations) {
                    if (!existingControl?.legalObligations?.includes(obligationId)) {
                        try {
                            const obligation = await (window as any).LegalObligationLibrary.LegalObligation.Get(obligationId);
                            const obligationData = await obligation.data;
                            const existingControls = obligationData.relatedControls || [];
                            if (!existingControls.includes(controlID)) {
                                existingControls.push(controlID);
                                await (window as any).LegalObligationLibrary.LegalObligation.Edit(obligationId, { relatedControls: existingControls });
                                console.log(`Added ${controlID} to obligation ${obligationId}`);
                            }
                        } catch (error) {
                            console.error(`Failed to add ${controlID} to obligation ${obligationId}:`, error);
                        }
                    }
                }
            }

            // Handle ISMS entries
            if (options.hasOwnProperty('ismsEntries')) {
                const removedEntries = existingControl?.ismsEntries?.filter(id => !options.ismsEntries.includes(id)) || [];
                for (const entryId of removedEntries) {
                    try {
                        const entry = await (window as any).MSRegister.Entry.Get(entryId);
                        const entryData = await entry.data;
                        const updatedControls = (entryData.relatedControls || []).filter(id => id !== controlID);
                        await (window as any).MSRegister.Entry.Edit(entryId, { relatedControls: updatedControls });
                        console.log(`Removed ${controlID} from ISMS entry ${entryId}`);
                    } catch (error) {
                        console.error(`Failed to remove ${controlID} from ISMS entry ${entryId}:`, error);
                    }
                }
                for (const entryId of options.ismsEntries) {
                    if (!existingControl?.ismsEntries?.includes(entryId)) {
                        try {
                            const entry = await (window as any).MSRegister.Entry.Get(entryId);
                            const entryData = await entry.data;
                            const existingControls = entryData.relatedControls || [];
                            if (!existingControls.includes(controlID)) {
                                existingControls.push(controlID);
                                await (window as any).MSRegister.Entry.Edit(entryId, { relatedControls: existingControls });
                                console.log(`Added ${controlID} to ISMS entry ${entryId}`);
                            }
                        } catch (error) {
                            console.error(`Failed to add ${controlID} to ISMS entry ${entryId}:`, error);
                        }
                    }
                }
            }
        }

        createToast({
            message: `Control ${controlID} saved successfully.`,
            customStyles: {
                backgroundColor: '#07B824',
                color: '#ffffff',
                zIndex: "9999999"
            },
        });
    } catch (error) {
        console.error(`Failed to save control ${controlID}:`, error);
        createToast({
            message: `Failed to save control ${controlID}.`,
            customStyles: {
                backgroundColor: '#D8090F',
                color: '#ffffff',
                zIndex: "9999999"
            },
        });
    }
}

/**
 * Saves data to multiple controls.
 * @param controlIDs An array of control IDs to save to.
 * @param options The data to save to the controls.
 * @param useISM Boolean to determine whether to use ISM or another path.
 */
export async function saveControls(
    controlIDs: string[],
    options: Record<string, any>,
    useISM: boolean = true,
    batchSize: number = 10, // Process in batches of 10 controls
    dontRunRelated = false
): Promise<void> {
    // Set the flag to disable onSnapshot updates
    isSavingControls = true;

    // Track progress
    let completed = 0;
    const total = controlIDs.length;

    const saveBatch = async (batch: string[]) => {
        for (const controlID of batch) {
            try {
                await saveControl(controlID, options, useISM, dontRunRelated);
                completed++;
            } catch (error) {
                console.error(`Failed to save control ${controlID}:`, error);
            }
        }
    };

    // Split controlIDs into batches
    for (let i = 0; i < controlIDs.length; i += batchSize) {
        const batch = controlIDs.slice(i, i + batchSize);
        await saveBatch(batch);
    }

    // Display success or failure message
    if (completed === total) {
        createToast({
            message: `All selected controls saved successfully.`,
            customStyles: {
                backgroundColor: '#07B824',
                color: '#ffffff',
            },
        });

        // Force onSnapshot reload
        setTimeout(() => {
            setupControlsListener(useISM); // Reinitialize the onSnapshot listener
            console.log("onSnapshot listener reloaded.");

            setTimeout(() => {
                closeModal(null, true);
            }, 500);
        }, 1000);
    } else {
        createToast({
            message: `Failed to save some controls.`,
            customStyles: {
                backgroundColor: '#D8090F',
                color: '#ffffff',
            },
        });
    }

    // Re-enable onSnapshot updates
    isSavingControls = false;
}

export async function saveControlsWithProgressBar(
    controlIDs: string[],
    options: Record<string, any>,
    selector: string = ".changesToBeMade",
    useISM: boolean = true,
    batchSize: number = 10 // Process in batches of 10 controls
): Promise<void> {
    // Set the flag to disable onSnapshot updates
    isSavingControls = true;

    // Create a progress bar element
    const progressBarContainer = document.createElement('div');
    progressBarContainer.style.position = 'relative';
    progressBarContainer.style.width = '100%';
    progressBarContainer.style.height = '16px';
    progressBarContainer.style.border = 'none';
    progressBarContainer.style.background = '#F1F1F1';
    progressBarContainer.style.borderRadius = '5px';
    progressBarContainer.style.overflow = 'hidden';

    const progressBar = document.createElement('div');
    progressBar.style.width = '0%';
    progressBar.style.height = '100%';
    progressBar.style.backgroundColor = '#F6961D';
    progressBar.style.transition = 'width 0.3s ease';

    progressBarContainer.appendChild(progressBar);

    // Append the progress bar to .changesToBeMade
    const changesContainer = document.querySelector(selector);
    if (changesContainer) {
        changesContainer.appendChild(progressBarContainer);
    }

    // Track progress
    let completed = 0;
    const total = controlIDs.length;

    const updateProgress = () => {
        completed++;
        const progressPercentage = (completed / total) * 100;
        progressBar.style.width = `${progressPercentage}%`;
    };

    const saveBatch = async (batch: string[]) => {
        for (const controlID of batch) {
            try {
                const control = (window as any).quickLookup[controlID];
                if (control) {
                    // Handle unmapping by comparing existing mappings with new options
                    const updatedOptions = { ...options };

                    // Get all checkboxes for this control
                    const checkboxNames = {
                        assets: "assetCheckboxes",
                        tasks: "taskCheckboxes",
                        supportingDocuments: "docCheckboxes",
                        policies: "policyCheckboxes",
                        relatedConfigurationStandard: "relatedConfigCheckboxes",
                        risks: "riskCheckboxes",
                        relatedControls: "relatedControlCheckboxes",
                        ismsEntries: "ismsEntryCheckboxes",
                        ismsObjectives: "ismsObjectiveCheckboxes",
                        legalObligations: "legalObligationCheckboxes",
                        suppliers: "supplierCheckboxes"
                    };

                    // For each mapping type, get the checked values
                    Object.entries(checkboxNames).forEach(([key, checkboxName]) => {
                        const checkboxes = document.querySelectorAll(`input[name="${checkboxName}"]:checked`);
                        // Always include the key in updatedOptions, with an empty array if no checkboxes are checked
                        updatedOptions[key] = Array.from(checkboxes).map((cb: HTMLInputElement) => cb.value);
                    });

                    let isISM = useISM;
                    if (control?.isISM !== undefined) {
                        isISM = control.isISM;
                    }
                    await saveControl(controlID, updatedOptions, isISM);
                }
                updateProgress();
            } catch (error) {
                console.error(`Failed to save control ${controlID}:`, error);
            }
        }
    };

    // Split controlIDs into batches
    for (let i = 0; i < controlIDs.length; i += batchSize) {
        const batch = controlIDs.slice(i, i + batchSize);
        await saveBatch(batch);
    }

    // Display success or failure message
    if (completed === total) {
        createToast({
            message: `All selected controls saved successfully.`,
            customStyles: {
                backgroundColor: '#07B824',
                color: '#ffffff',
            },
        });

        // Force onSnapshot reload
        setTimeout(() => {
            if (!useISM) {
                // Refresh the controls list
                fetchControls(false).then(async ({ ConfigurationStandards, Controls }) => {
                    (window as any).mcmControls = Controls; // Store globally if needed
                    (window as any).configurationStandards = ConfigurationStandards;
                    renderControlsTable((window as any).mcmControls, false);
                    const accordions = new Tableordion(".tableordion-row");
                });
            } else {
                setupControlsListener(useISM); // Reinitialize the onSnapshot listener
                console.log("onSnapshot listener reloaded.");
            }

            if (progressBarContainer && changesContainer) {
                changesContainer.removeChild(progressBarContainer);
            }

            setTimeout(() => {
                closeModal(null, true);
            }, 500);
        }, 1000);
    } else {
        createToast({
            message: `Failed to save some controls.`,
            customStyles: {
                backgroundColor: '#D8090F',
                color: '#ffffff',
            },
        });

        if (progressBarContainer && changesContainer) {
            progressBar.style.background = '#D8090F';
        }
    }

    // Re-enable onSnapshot updates
    isSavingControls = false;
}

(window as any).generateShareableLink = async () => {
    try {
        let configurationStandards = (window as any).configurationStandards;

        // If window.configurationStandards doesn't exist, fetch all configuration standards
        if (!configurationStandards || Object.keys(configurationStandards).length === 0) {
            configurationStandards = {};
            const controls = (window as any).mcmControls || [];
            for (let i = 0; i < controls.length; i++) {
                const control = controls[i];
                const standardNames = Array.isArray(control.relatedConfigurationStandard)
                    ? control.relatedConfigurationStandard
                    : [control.relatedConfigurationStandard || 'Unassigned'];
                for (let j = 0; j < standardNames.length; j++) {
                    let standardName = standardNames[j];
                    if (!configurationStandards[standardName]) {
                        configurationStandards[standardName] = {
                            configurationStandard: standardName,
                            controls: [],
                        };
                    }
                    configurationStandards[standardName].controls.push({ controlID: control.controlID });
                }
            }
        } else {
            // Only include control IDs for the existing configuration standards
            configurationStandards = Object.fromEntries(
                Object.entries(configurationStandards).map(([key, standard]) => [
                    key,
                    {
                        configurationStandard: (standard as ConfigurationStandard).configurationStandard,
                        controls: (standard as ConfigurationStandard).controls.map((control: ControlData) => ({
                            controlID: control.controlID,
                        })),
                    },
                ])
            );
        }

        // Confirmation modal before generating the link
        const { isConfirmed } = await Swal.fire({
            title: 'Share Configuration Standards',
            html: `
                <p>This will share all configuration standards and their associated control IDs.</p>
                <p>Are you sure you want to generate a shareable link?</p>
            `,
            icon: 'info',
            showCancelButton: true,
            confirmButtonText: 'Generate Link',
            cancelButtonText: 'Cancel',
        });

        if (!isConfirmed) {
            return; // Exit if the user cancels
        }

        // Store the configuration standards in Firestore
        const docRef = await db.collection('sharedConfigurations').add({ configurationStandards });
        const shareableLink = `${window.location.origin}/configurationStandards#!/ConfigurationStandards/?id=${docRef.id}`;

        // Show the shareable link
        await Swal.fire({
            title: 'Shareable Link',
            html: `<p>Copy the link below to share all configuration standards:</p>
                   <input type="text" class="swal2-input" value="${shareableLink}" readonly />`,
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: 'Copy Link',
            preConfirm: () => {
                navigator.clipboard.writeText(shareableLink);
            },
        });
    } catch (error) {
        console.error('Error generating shareable link:', error);
        createToast({
            message: 'Failed to generate shareable link.',
            customStyles: {
                backgroundColor: '#D8090F',
                color: '#ffffff',
            },
        });
    }
};

(window as any).applySharedConfiguration = async (uniqueId: string) => {
    try {
        // Fetch the configuration data using the unique ID
        const doc = await db.collection('sharedConfigurations').doc(uniqueId).get();

        if (!doc.exists) {
            throw new Error('Configuration not found.');
        }

        const { configurationStandards } = doc.data();

        // Preview all configuration standards and their associated control IDs
        const previewHTML = Object.values(configurationStandards)
            .sort((a, b) => (a as ConfigurationStandard).configurationStandard.localeCompare((b as ConfigurationStandard).configurationStandard))
            .map((standard: ConfigurationStandard) => {
                const controlsHTML = standard.controls
                    .map(
                        (control: { controlID: string }) => `
                        <tr class="tableordion-row">
                            <td>${control.controlID}</td>
                        </tr>
                    `
                    )
                    .join('');

                return `
                <div>
                    <h4>${standard.configurationStandard}</h4>
                    <table class="tableordion">
                        <thead class="tableordion-header">
                            <tr>
                                <th>Control ID</th>
                            </tr>
                        </thead>
                        <tbody class="tableordion-body">
                            ${controlsHTML}
                        </tbody>
                    </table>
                </div>`;
            })
            .join('');

        // Show preview and confirm application
        const { isConfirmed } = await Swal.fire({
            title: 'Apply Configuration Standards',
            html: `<p>Note: This will take a while if you have a lot of controls</p><p>Preview the configuration standards and their associated control IDs:</p>${previewHTML}`,
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: 'Apply All',
            customClass: {
                container: 'selectControls-container',
            },
            didRender: () => {
                new Tableordion('.swal2-container .tableordion-row');
                addPagerToTables('.swal2-container .tableordion');
            },
        });

        if (isConfirmed) {
            showLoadingScreen();
            const updatePromises = Object.values(configurationStandards).flatMap((standard: ConfigurationStandard) =>
                standard.controls.map((control: { controlID: string }) =>
                    saveControl(control.controlID, { relatedConfigurationStandard: standard.configurationStandard })
                )
            );

            await Promise.all(updatePromises);
            hideLoadingScreen();
            createToast({
                message: `All configuration standards applied successfully.`,
                customStyles: {
                    backgroundColor: '#07B824',
                    color: '#ffffff',
                },
            });

            setupControlsListener(); // Reload controls to reflect changes
        }
    } catch (error) {
        console.error('Error applying shared configuration standards:', error);
        createToast({
            message: 'Failed to apply configuration standards.',
            customStyles: {
                backgroundColor: '#D8090F',
                color: '#ffffff',
            },
        });
    }
};

(window as any).generateStandardsLink = async () => {
    try {
        const selectedControlIds = (window as any).mcmControlsToUpdate || [];
        if (selectedControlIds.length === 0) {
            createToast({
                message: 'Please select controls to share.',
                customStyles: {
                    backgroundColor: '#D8090F',
                    color: '#ffffff',
                },
            });
            return;
        }

        const controlsArray = (window as any).mcmControls || [];
        const quickLookup = (window as any).quickLookup || {};

        const selectedControls = selectedControlIds.map(controlID => {
            // Try to find the control in the array
            let control = controlsArray.find(ctrl => ctrl.controlID === controlID);

            // If not found in array, check quickLookup object
            if (!control && quickLookup[controlID]) {
                control = quickLookup[controlID];
            }

            return control;
        }).filter(Boolean); // Remove any `undefined` entries

        console.log(selectedControls);

        // Group selected controls by standard
        const standardsMap = new Map();
        selectedControls.forEach(control => {
            if (!standardsMap.has(control.standard)) {
                standardsMap.set(control.standard, {
                    standard: control.standard,
                    controls: []
                });
            }
            // Include all control data except for large arrays that might not be needed
            const controlData = {
                standard: control.standard,
                section: control.section,
                topic: control.topic,
                controlID: control.controlID,
                controlDescription: control.controlDescription,
                controlOwner: control.controlOwner,
                controlApplicable: control.controlApplicable,
                isISM: control.isISM || false
            };
            standardsMap.get(control.standard).controls.push(controlData);
        });

        const standards = Object.fromEntries(standardsMap);

        // Confirmation modal before generating the link
        const { isConfirmed } = await Swal.fire({
            title: 'Share Standards/Frameworks',
            html: `
                <p>This will share all standards/frameworks and their associated controls.</p>
                <p>The following standards will be shared:</p>
                <ul>
                    ${Array.from(standardsMap.keys()).map(standard => `<li>${standard}</li>`).join('')}
                </ul>
                <p>Are you sure you want to generate a shareable link?</p>
            `,
            icon: 'info',
            showCancelButton: true,
            confirmButtonText: 'Generate Link',
            cancelButtonText: 'Cancel',
        });

        if (!isConfirmed) {
            return;
        }

        // Store the standards in Firestore
        const docRef = await db.collection('sharedStandards').add({ standards });
        const shareableLink = `${window.location.origin}/masterControlMapping#!/MasterControlMapping/?id=${docRef.id}`;

        // Show the shareable link
        await Swal.fire({
            title: 'Shareable Link',
            html: `<p>Copy the link below to share all standards/frameworks:</p>
                   <input type="text" class="swal2-input" value="${shareableLink}" readonly />`,
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: 'Copy Link',
            preConfirm: () => {
                navigator.clipboard.writeText(shareableLink);
            },
        });
    } catch (error) {
        console.error('Error generating standards link:', error);
        createToast({
            message: 'Failed to generate standards link.',
            customStyles: {
                backgroundColor: '#D8090F',
                color: '#ffffff',
            },
        });
    }
};

(window as any).applySharedStandards = async (uniqueId: string) => {
    try {
        // Fetch the standards data using the unique ID
        const doc = await db.collection('sharedStandards').doc(uniqueId).get();

        if (!doc.exists) {
            throw new Error('Standards not found.');
        }

        const data = doc.data();
        if (!data) throw new Error('No data found');

        const standards = data.standards as Standards;

        // Preview all standards and their controls
        const previewHTML = Object.values(standards)
            .sort((a, b) => a.standard.localeCompare(b.standard))
            .map((standard: StandardData) => {
                const controlsHTML = standard.controls
                    .map(
                        (control) => `
                        <tr class="tableordion-row">
                            <td>${control.controlID}</td>
                            <td>${control.section}</td>
                            <td>${truncateTo15Words(control.controlDescription)}</td>
                        </tr>
                    `
                    )
                    .join('');

                return `
                <div>
                    <h4>${standard.standard}</h4>
                    <table class="tableordion">
                        <thead class="tableordion-header">
                            <tr>
                                <th>Control ID</th>
                                <th>Section</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody class="tableordion-body">
                            ${controlsHTML}
                        </tbody>
                    </table>
                </div>`;
            })
            .join('');

        // Show preview and confirm application
        const { isConfirmed } = await Swal.fire({
            title: 'Apply Standards/Frameworks',
            html: `
                <p>Note: This will take a while if you have a lot of controls</p>
                <p>Preview the standards/frameworks and their controls:</p>
                ${previewHTML}
            `,
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: 'Apply All',
            customClass: {
                container: 'selectControls-container',
            },
            didRender: () => {
                new Tableordion('.swal2-container .tableordion-row');
                addPagerToTables('.swal2-container .tableordion');
            },
        });

        if (isConfirmed) {
            showLoadingScreen();
            const updatePromises = Object.values(standards).flatMap((standard) =>
                standard.controls.map((control) =>
                    saveControl(control.controlID, {
                        standard: control.standard,
                        section: control.section,
                        topic: control.topic,
                        controlDescription: control.controlDescription,
                        controlOwner: control.controlOwner,
                        controlApplicable: control.controlApplicable
                    }, control.isISM, true)
                )
            );

            await Promise.all(updatePromises);
            hideLoadingScreen();
            createToast({
                message: `All standards/frameworks applied successfully.`,
                customStyles: {
                    backgroundColor: '#07B824',
                    color: '#ffffff',
                },
            });

            setupControlsListener(); // Reload controls to reflect changes
        }
    } catch (error) {
        console.error('Error applying shared standards:', error);
        createToast({
            message: 'Failed to apply standards/frameworks.',
            customStyles: {
                backgroundColor: '#D8090F',
                color: '#ffffff',
            },
        });
    }
};